import { CONSENT_EVENT } from '../components/PostHogProvider';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MessageSquare, Zap, Users, BarChart3, Globe, ArrowRight, Check, Star, Mail, Send, UserPlus, Code, Plug, MessageCircle, Youtube, Instagram, Facebook, Sparkles, TrendingUp, Shield, Moon, Sun, Menu, X, Cookie, Settings, Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { PricingPlans } from '../components/PricingPlans';
import BlogSection from '../components/BlogSection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { toast } from 'sonner';
import api from '../api/axios';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const LandingPageTranslations = {
  tr: {
    brand: 'FeedPanels',
    tagline: 'Müşteri Feedback Platformu',
    heroTitle: 'Müşteri Feedback\'ini Yönetin',
    heroDescription: 'Müşteri geri bildirimini toplayın, organize edin ve harita haline dönüştürün. Tüm müşteri sesini bir yerde yönetin.',
    heroBadge: 'Müşteri Geri Bildirimlerini Yönetin',
    heroTrust1: 'Güvenli ve Güvenilir',
    heroTrust2: 'Hızlı Entegrasyon',
    heroTrust3: 'Binlerce Mutlu Müşteri',
    getStarted: 'Ücretsiz Başla',
    getStartedShort: 'Başla',
    signIn: 'Giriş Yap',
    signInShort: 'Giriş',
    heroBadgeShort: 'Feedback Yönetimi',
    navFeatures: 'Özellikler',
    navHowItWorks: 'Nasıl Çalışır',
    navPricing: 'Fiyatlandırma',
    navBlog: 'Blog',
    navFAQ: 'SSS',
    featuresTitle: 'Neden FeedPanels?',
    feature1Title: 'Basit Entegrasyon',
    feature1Desc: 'Feedback widget\'ini 2 dakikada web sitenize entegre edin',
    feature2Title: 'Akıllı Kategorizasyon',
    feature2Desc: 'Feedback\'leri otomatik olarak kategorize edin ve önceliklendirin',
    feature3Title: 'Şeffaf Yol Haritası',
    feature3Desc: 'Müşterilerinize ürün gelişimini gerçek zamanlı olarak gösterin',
    feature4Title: 'Yayın Notları',
    feature4Desc: 'Yeni özellikleri duyurun ve değişiklikleri izleyin',
    feature5Title: 'Takım İşbirliği',
    feature5Desc: 'Tüm takımınız feedback\'leri görüntüleyebilir ve yorum yapabilir',
    feature6Title: 'Gelişmiş Analizler',
    feature6Desc: 'Feedback eğilimlerini analiz edin ve data-driven kararlar alın',
    pricingTitle: 'Fiyatlandırma',
    starterPlan: 'Başlangıç',
    starterPrice: '€29',
    starterDesc: 'Küçük projeler için mükemmel',
    premiumPlan: 'Profesyonel',
    premiumPrice: '€79',
    premiumDesc: 'Büyüyen işletmeler için',
    enterprisePlan: 'Kurumsal',
    enterprisePrice: 'Özel Fiyat',
    enterpriseDesc: 'Özel ihtiyaçlar için',
    perMonth: '/ay',
    planFeatures: [
      'Feedback Widget',
      'Roadmap & Changelog',
      'Temel Analizler',
      'E-posta Desteği',
      'Özel Domain'
    ],
    ctaTitle: 'Müşteri Geri Bildiriminizi Bugün Optimize Edin',
    ctaDescription: 'FeedPanels ile müşterilerinizin sesini dinleyin ve işletmenizi geliştirin',
    faqTitle: 'Sıkça Sorulan Sorular',
    faqSubtitle: 'Merak ettikleriniz hakkında bilgi alın',
    faqTabProject: 'Proje',
    faqTabPayment: 'Ödeme',
    // Project FAQs
    faq1Question: 'FeedPanels nasıl çalışır?',
    faq1Answer: 'FeedPanels, web sitenize entegre edebileceğiniz bir feedback widget\'i sağlar. Müşterileriniz bu widget üzerinden geri bildirimlerini gönderebilir ve siz bu geri bildirimleri dashboard\'unuzda görüntüleyip yönetebilirsiniz.',
    faq2Question: 'Entegrasyon ne kadar sürer?',
    faq2Answer: 'Entegrasyon çok basittir. Sadece birkaç satır kod eklemeniz yeterlidir ve 2 dakikadan kısa sürede tamamlanabilir.',
    faq3Question: 'Widget\'i özelleştirebilir miyim?',
    faq3Answer: 'Evet, widget\'in renklerini, konumunu ve görünümünü dashboard\'unuzdan kolayca özelleştirebilirsiniz.',
    faq4Question: 'Verilerim güvende mi?',
    faq4Answer: 'Evet, tüm verileriniz SSL şifreleme ile korunur ve GDPR uyumludur. Verilerinizin güvenliği bizim için çok önemlidir.',
    faq5Question: 'Hangi web siteleri ile uyumlu?',
    faq5Answer: 'FeedPanels tüm modern web siteleri ile uyumludur. React, Vue, Next.js, Nuxt, WordPress ve JavaScript ile entegrasyon sağlar. Tek satır kod ile entegre edebilirsiniz.',
    faq6Question: 'Geri bildirimleri nasıl yönetirim?',
    faq6Answer: 'Bildirimler feedback ve roadmap menüsünde görebilirsiniz. Dilerseniz roadmap\'de feedback\'in konumunu güncelleyerek müşterilerinizi bilgilendirebilirsiniz.',
    // Payment FAQs
    faqPayment1Question: 'Hangi ödeme yöntemlerini kabul ediyorsunuz?',
    faqPayment1Answer: 'Kredi kartı, banka kartı ve PayPal ile ödeme yapabilirsiniz. Tüm ödemeler güvenli bir şekilde işlenir.',
    faqPayment2Question: 'Ücretsiz deneme var mı?',
    faqPayment2Answer: 'Evet, kayıt olduktan sonra 14 günlük ücretsiz deneme süreniz olur. Bu süre içinde tüm özellikleri test edebilirsiniz.',
    faqPayment3Question: 'Fiyatlandırma nasıl çalışır?',
    faqPayment3Answer: 'Aylık veya yıllık abonelik seçeneklerimiz bulunmaktadır. Yıllık ödemelerde %20 indirim kazanırsınız. İstediğiniz zaman planınızı değiştirebilir veya iptal edebilirsiniz.',
    faqPayment4Question: 'İptal edebilir miyim?',
    faqPayment4Answer: 'Evet, istediğiniz zaman aboneliğinizi iptal edebilirsiniz. İptal işlemi anında geçerli olur ve bir sonraki faturalama döneminden itibaren ücretlendirilmezsiniz.',
    faqPayment5Question: 'İstediğim plana yükseltebilir miyim?',
    faqPayment5Answer: 'Evet, anında istediğiniz plana yükseltebilirsiniz.',
    faqPayment6Question: 'Fiyatlar vergi dahil mi?',
    faqPayment6Answer: 'Evet, fiyatlarımız vergi dahildir.',
    footerText: '© 2025 ZeuZ IT tarafından oluşturuldu',
    footerDescription: 'Müşteri geri bildirimlerinizi yönetin ve işletmenizi büyütün.',
    footerProduct: 'Ürün',
    footerCompany: 'Şirket',
    footerResources: 'Kaynaklar',
    footerLegal: 'Yasal',
    footerFollowUs: 'Bizi Takip Edin',
    footerLinks: {
      features: 'Özellikler',
      pricing: 'Fiyatlandırma',
      blog: 'Blog',
      contact: 'İletişim',
      privacy: 'Gizlilik Politikası',
      terms: 'Kullanım Şartları'
    },
    // Contact Form
    contactSection: 'Bize Ulaşın',
    contactSectionDesc: 'Sorularınız mı var? Bize yazın ve en kısa sürede yanıt alalım.',
    yourName: 'Adınız',
    yourNamePlaceholder: 'Adınızı girin',
    email: 'E-posta',
    emailPlaceholder: 'email@example.com',
    subject: 'Konu',
    subjectPlaceholder: 'Mesaj konusu',
    message: 'Mesajınız',
    messagePlaceholder: 'Mesajınızı buraya yazın...',
    sendMessage: 'Mesaj Gönder',
    sending: 'Gönderiliyor...',
    messageSent: 'Mesajınız başarıyla gönderildi!',
    messageFailed: 'Mesaj gönderilemedi. Lütfen tekrar deneyin.',
    allFieldsRequired: 'Lütfen tüm alanları doldurun',
    fieldRequiredName: 'Lütfen adınızı girin',
    fieldRequiredEmail: 'Lütfen e-posta adresinizi girin',
    fieldRequiredSubject: 'Lütfen konuyu girin',
    fieldRequiredMessage: 'Lütfen mesajınızı girin',
    acceptPrivacyPolicy: 'Gizlilik Politikasını okudum ve verilerimin talebimin işlenmesi için kullanılmasını kabul ediyorum.',
    privacyPolicyRequired: 'Gizlilik Politikası\'nı kabul etmelisiniz.',
    recaptchaRequired: 'Lütfen reCAPTCHA\'yı tamamlayın',
    videoSectionTitle: 'FeedPanels\'ı Keşfedin',
    videoSectionSubtitle: 'FeedPanels\'ın nasıl çalıştığını dakikalar içinde görün',
    videoSectionLoading: 'Yükleniyor...',
    // How It Works Section
    howItWorksTitle: 'Nasıl Kullanılır?',
    howItWorksSubtitle: 'Sadece 4 basit adımda başlayın',
    step1Title: 'Kayıt Ol',
    step1Desc: 'Ücretsiz hesap oluşturun ve dakikalar içinde başlayın',
    step2Title: 'Entegrasyon Kodunu Al',
    step2Desc: 'Dashboard\'dan widget entegrasyon kodunu kopyalayın',
    step3Title: 'Projene Entegre Et',
    step3Desc: 'Tek satır kod ile web sitenize widget\'i ekleyin',
    step4Title: 'Geri Bildirimleri Al',
    step4Desc: 'Müşterilerinizin geri bildirimlerini anlık olarak görüntüleyin',
    featureFeedbackTitle: 'Geri Bildirim Yönetimi',
    featureFeedbackDesc: 'Müşterilerinizden gelen tüm geri bildirimleri tek bir yerde toplayın, kategorize edin ve yönetin. Geri bildirimleri önceliklendirin ve takımınızla paylaşın.',
    featureRoadmapTitle: 'Yol Haritası',
    featureRoadmapDesc: 'Planlanan özellikleri ve geliştirmeleri görsel bir yol haritasında sunun. Müşterileriniz neyin geldiğini görebilir ve heyecanlanabilir.',
    featureChangelogTitle: 'Değişiklik Günlüğü',
    featureChangelogDesc: 'Yaptığınız tüm güncellemeleri ve iyileştirmeleri takip edin. Müşterilerinize neyin değiştiğini kolayca bildirin.',
    // Testimonials Section
    testimonialsTitle: 'Müşterilerimiz Ne Diyor?',
    testimonialsSubtitle: 'Binlerce mutlu müşterimizden bazıları',
    testimonial1Name: 'Ahmet Y.',
    testimonial1Company: 'Teknoloji Startup',
    testimonial1Text: 'FeedPanels müşteri geri bildirimlerini yönetmeyi inanılmaz kolay hale getirdi. Müşteri memnuniyeti %40 arttı.',
    testimonial2Name: 'Ayşe K.',
    testimonial2Company: 'SaaS Şirketi',
    testimonial2Text: 'Widget entegrasyonu çok basit, tüm ekibimiz geri bildirimleri hızlıca görebiliyor. Harika bir araç!',
    testimonial3Name: 'Mehmet T.',
    testimonial3Company: 'E-Ticaret',
    testimonial3Text: 'Geri bildirim analitiği sayesinde ürün geliştirme sürecimiz %50 hızlandı. Çok seviyoruz!',
    testimonial4Name: 'Zeynep A.',
    testimonial4Company: 'Dijital Ajans',
    testimonial4Text: 'Müşteri geri bildirimlerini toplamak ve yönetmek artık çok kolay. Ekibimiz ve müşterilerimiz çok memnun!',
    // Cookie Consent
    cookieTitle: 'Çerez Ayarları',
    cookieDescription: 'Bu web sitesinde mükemmel olan en iyi deneyimi sunmak için çerezler kullanıyoruz.',
    cookieDescription2: 'Bazı çerezler web sitesinin çalışması için gereklidir, diğerleri ise bu web sitesi ve kullanıcı deneyimini iyileştirmemize yardımcı olur.',
    cookieDescription3: 'Çerezleri izin vermeyeceğinize kendiniz karar verebilirsiniz. Lütfen reddetmeniz durumunda web sitesinin işlevselliğinin sınırlı olabileceğini unutmayın.',
    cookieSettings: 'Ayarlar',
    cookieRejectAll: 'Tümünü Reddet',
    cookieAcceptAll: 'Tümünü Kabul Et',
    cookieEditTitle: 'Çerez Ayarlarını Düzenle',
    cookieEditDescription: 'Hangi çerezleri izin istediğinizi seçin. Gerekli çerezler, web sitesinin temel işlevleri için gereklidir ve devre dışı bırakılamaz.',
    cookieNecessary: 'Gerekli Çerezler',
    cookieNecessaryDesc: 'Bu çerezler web sitesinin temel işlevleri için gereklidir ve devre dışı bırakılamaz.',
    cookieFunctional: 'İşlevsel Çerezler',
    cookieFunctionalDesc: 'Bu çerezler, web sitesinin gelişmiş işlevselliğe ve kişiselleştirme sağlamasına olanak tanır.',
    cookieAnalytical: 'Analitik Çerezler',
    cookieAnalyticalDesc: 'Bu çerezler, ziyaretçilerin web sitesiyle nasıl etkileşimde bulunduğunu anlamamamıza yardımcı olur, bilgileri anonim olarak toplayarak ve raporlayarak.',
    cookieMarketing: 'Pazarlama Çerezleri',
    cookieMarketingDesc: 'Bu çerezler, ziyaretçilere web siteleri arasında ilgili reklam ve pazarlama kampanyaları sağlamak için kullanılır.',
    cookieBack: 'Geri',
    cookieSave: 'Ayarları Kaydet',
    cookiePrivacyPolicy: 'Gizlilik Politikası',
    cookieLegalNotice: 'Yasal Uyarı',
  },
  en: {
    brand: 'FeedPanels',
    tagline: 'Customer Feedback Platform',
    heroTitle: 'Manage Customer Feedback',
    heroDescription: 'Collect, organize, and transform customer feedback. Manage all customer voices in one place.',
    heroBadge: 'Manage Customer Feedback',
    heroTrust1: 'Secure & Reliable',
    heroTrust2: 'Quick Integration',
    heroTrust3: 'Thousands of Happy Customers',
    getStarted: 'Get Started Free',
    getStartedShort: 'Start',
    signIn: 'Sign In',
    signInShort: 'Sign In',
    heroBadgeShort: 'Feedback Management',
    navFeatures: 'Features',
    navHowItWorks: 'How It Works',
    navPricing: 'Pricing',
    navBlog: 'Blog',
    navFAQ: 'FAQ',
    featuresTitle: 'Why Choose FeedPanels?',
    feature1Title: 'Simple Integration',
    feature1Desc: 'Integrate feedback widget to your website in just 2 minutes',
    feature2Title: 'Smart Categorization',
    feature2Desc: 'Automatically categorize and prioritize feedback',
    feature3Title: 'Transparent Roadmap',
    feature3Desc: 'Show your customers your product development in real-time',
    feature4Title: 'Release Notes',
    feature4Desc: 'Announce new features and track changes',
    feature5Title: 'Team Collaboration',
    feature5Desc: 'Your entire team can view and comment on feedback',
    feature6Title: 'Advanced Analytics',
    feature6Desc: 'Analyze feedback trends and make data-driven decisions',
    pricingTitle: 'Pricing',
    starterPlan: 'Starter',
    starterPrice: '€29',
    starterDesc: 'Perfect for small projects',
    premiumPlan: 'Professional',
    premiumPrice: '€79',
    premiumDesc: 'For growing businesses',
    enterprisePlan: 'Enterprise',
    enterprisePrice: 'Custom',
    enterpriseDesc: 'For special needs',
    perMonth: '/month',
    planFeatures: [
      'Feedback Widget',
      'Roadmap & Changelog',
      'Basic Analytics',
      'Email Support',
      'Custom Domain'
    ],
    ctaTitle: 'Optimize Your Customer Feedback Today',
    ctaDescription: 'Listen to your customers\' voices with FeedPanels and grow your business',
    faqTitle: 'Frequently Asked Questions',
    faqSubtitle: 'Get answers to your questions',
    faqTabProject: 'Project',
    faqTabPayment: 'Payment',
    // Project FAQs
    faq1Question: 'How does FeedPanels work?',
    faq1Answer: 'FeedPanels provides a feedback widget that you can integrate into your website. Your customers can submit their feedback through this widget, and you can view and manage these feedbacks in your dashboard.',
    faq2Question: 'How long does integration take?',
    faq2Answer: 'Integration is very simple. You just need to add a few lines of code and it can be completed in less than 2 minutes.',
    faq3Question: 'Can I customize the widget?',
    faq3Answer: 'Yes, you can easily customize the widget\'s colors, position, and appearance from your dashboard.',
    faq4Question: 'Is my data secure?',
    faq4Answer: 'Yes, all your data is protected with SSL encryption and is GDPR compliant. The security of your data is very important to us.',
    faq5Question: 'Which websites is it compatible with?',
    faq5Answer: 'FeedPanels is compatible with all modern websites. It works with React, Vue, Next.js, Nuxt, WordPress and JavaScript. You can integrate it with a single line of code.',
    faq6Question: 'How do I manage feedback?',
    faq6Answer: 'You can view feedback in the feedback and roadmap menus. If you wish, you can update the feedback position in the roadmap to keep your customers informed.',
    // Payment FAQs
    faqPayment1Question: 'What payment methods do you accept?',
    faqPayment1Answer: 'You can pay with credit card, debit card, and PayPal. All payments are processed securely.',
    faqPayment2Question: 'Is there a free trial?',
    faqPayment2Answer: 'Yes, after signing up, you get a 14-day free trial period. You can test all features during this time.',
    faqPayment3Question: 'How does pricing work?',
    faqPayment3Answer: 'We offer monthly or annual subscription options. Annual payments save you 20%. You can change or cancel your plan at any time.',
    faqPayment4Question: 'Can I cancel?',
    faqPayment4Answer: 'Yes, you can cancel your subscription at any time. Cancellation takes effect immediately and you will not be charged from the next billing period.',
    faqPayment5Question: 'Can I upgrade to a plan I want?',
    faqPayment5Answer: 'Yes, you can upgrade to any plan you want instantly.',
    faqPayment6Question: 'Are prices tax inclusive?',
    faqPayment6Answer: 'Yes, our prices are tax inclusive.',
    testimonial1Name: 'John D.',
    testimonial1Company: 'Tech Startup',
    testimonial1Text: 'FeedPanels made managing customer feedback incredibly easy. Customer satisfaction increased by 40%.',
    testimonial2Name: 'Sarah M.',
    testimonial2Company: 'SaaS Company',
    testimonial2Text: 'Widget integration is very simple, our entire team can see feedback quickly. Amazing tool!',
    testimonial3Name: 'Mike T.',
    testimonial3Company: 'E-Commerce',
    testimonial3Text: 'Thanks to feedback analytics, our product development process accelerated by 50%. Love it!',
    testimonial4Name: 'Emma L.',
    testimonial4Company: 'Digital Agency',
    testimonial4Text: 'Collecting and managing customer feedback is now so easy. Our team and customers are very satisfied!',
    // Cookie Consent
    cookieTitle: 'Cookie Settings',
    cookieDescription: 'We use cookies to provide the best possible experience on this website.',
    cookieDescription2: 'Some cookies are necessary for the website to function, while others help us improve this website and the user experience.',
    cookieDescription3: 'You can decide for yourself whether you will not allow cookies. Please note that if you reject them, the website\'s functionality may be limited.',
    cookieSettings: 'Settings',
    cookieRejectAll: 'Reject All',
    cookieAcceptAll: 'Accept All',
    cookieEditTitle: 'Edit Cookie Settings',
    cookieEditDescription: 'Choose which cookies you want to allow. Necessary cookies are essential for the basic functions of the website and cannot be disabled.',
    cookieNecessary: 'Necessary Cookies',
    cookieNecessaryDesc: 'These cookies are essential for the basic functions of the website and cannot be disabled.',
    cookieFunctional: 'Functional Cookies',
    cookieFunctionalDesc: 'These cookies enable advanced functionality and personalization of the website.',
    cookieAnalytical: 'Analytical Cookies',
    cookieAnalyticalDesc: 'These cookies help us understand how visitors interact with the website by collecting and reporting information anonymously.',
    cookieMarketing: 'Marketing Cookies',
    cookieMarketingDesc: 'These cookies are used to provide relevant advertisements and marketing campaigns to visitors across websites.',
    cookieBack: 'Back',
    cookieSave: 'Save Settings',
    cookiePrivacyPolicy: 'Privacy Policy',
    cookieLegalNotice: 'Legal Notice',
    footerText: '© 2025 Created by ZeuZ IT',
    footerDescription: 'Manage your customer feedback and grow your business.',
    footerProduct: 'Product',
    footerCompany: 'Company',
    footerResources: 'Resources',
    footerLegal: 'Legal',
    footerFollowUs: 'Follow Us',
    footerLinks: {
      features: 'Features',
      pricing: 'Pricing',
      blog: 'Blog',
      contact: 'Contact',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service'
    },
    // Contact Form
    contactSection: 'Contact Us',
    contactSectionDesc: 'Have questions? Write to us and get an answer as soon as possible.',
    yourName: 'Your Name',
    yourNamePlaceholder: 'Enter your name',
    email: 'Email',
    emailPlaceholder: 'email@example.com',
    subject: 'Subject',
    subjectPlaceholder: 'Message subject',
    message: 'Message',
    messagePlaceholder: 'Write your message here...',
    sendMessage: 'Send Message',
    sending: 'Sending...',
    messageSent: 'Your message has been sent successfully!',
    messageFailed: 'Message could not be sent. Please try again.',
    allFieldsRequired: 'Please fill in all fields',
    fieldRequiredName: 'Please enter your name',
    fieldRequiredEmail: 'Please enter your email address',
    fieldRequiredSubject: 'Please enter the subject',
    fieldRequiredMessage: 'Please enter your message',
    acceptPrivacyPolicy: 'I have read the Privacy Policy and accept that my data will be used to process my request.',
    privacyPolicyRequired: 'You must accept the Privacy Policy.',
    recaptchaRequired: 'Please complete the reCAPTCHA',
    videoSectionTitle: 'See FeedPanels in Action',
    videoSectionSubtitle: 'See how FeedPanels works in minutes',
    videoSectionLoading: 'Loading...',
    // How It Works Section
    howItWorksTitle: 'How It Works?',
    howItWorksSubtitle: 'Get started in just 4 simple steps',
    step1Title: 'Sign Up',
    step1Desc: 'Create a free account and get started in minutes',
    step2Title: 'Get Integration Code',
    step2Desc: 'Copy the widget integration code from your dashboard',
    step3Title: 'Integrate to Your Project',
    step3Desc: 'Add the widget to your website with a single line of code',
    step4Title: 'Receive Feedback',
    step4Desc: 'View your customers\' feedback in real-time',
    featureFeedbackTitle: 'Feedback Management',
    featureFeedbackDesc: 'Collect, categorize, and manage all customer feedback in one place. Prioritize feedback and share with your team.',
    featureRoadmapTitle: 'Roadmap',
    featureRoadmapDesc: 'Present planned features and improvements in a visual roadmap. Your customers can see what\'s coming and get excited.',
    featureChangelogTitle: 'Changelog',
    featureChangelogDesc: 'Track all your updates and improvements. Easily inform your customers about what has changed.',
    // Testimonials Section
    testimonialsTitle: 'What Our Customers Say',
    testimonialsSubtitle: 'Some of our thousands of happy customers',
  },
  de: {
    brand: 'FeedPanels',
    tagline: 'Kundenbedarfs-Plattform',
    heroTitle: 'Verwalten Sie Kundenfeedback',
    heroDescription: 'Sammeln, organisieren und transformieren Sie Kundenfeedback. Verwalten Sie alle Kundenstimmen an einem Ort.',
    heroBadge: 'Verwalten Sie Kundenfeedback',
    heroTrust1: 'Sicher & Zuverlässig',
    heroTrust2: 'Schnelle Integration',
    heroTrust3: 'Tausende zufriedene Kunden',
    getStarted: 'Kostenlos Starten',
    getStartedShort: 'Starten',
    signIn: 'Anmelden',
    signInShort: 'Anmelden',
    heroBadgeShort: 'Feedback-Verwaltung',
    navFeatures: 'Funktionen',
    navHowItWorks: 'Wie es funktioniert',
    navPricing: 'Preise',
    navBlog: 'Blog',
    navFAQ: 'FAQ',
    featuresTitle: 'Warum FeedPanels?',
    feature1Title: 'Einfache Integration',
    feature1Desc: 'Integrieren Sie das Feedback-Widget in nur 2 Minuten auf Ihre Website',
    feature2Title: 'Intelligente Kategorisierung',
    feature2Desc: 'Kategorisieren und priorisieren Sie Feedback automatisch',
    feature3Title: 'Transparente Roadmap',
    feature3Desc: 'Zeigen Sie Ihren Kunden Ihre Produktentwicklung in Echtzeit',
    feature4Title: 'Versionshinweise',
    feature4Desc: 'Kündigen Sie neue Funktionen an und verfolgen Sie Änderungen',
    feature5Title: 'Teamzusammenarbeit',
    feature5Desc: 'Ihr gesamtes Team kann Feedback ansehen und kommentieren',
    feature6Title: 'Erweiterte Analytik',
    feature6Desc: 'Analysieren Sie Feedback-Trends und treffen Sie datengesteuerte Entscheidungen',
    pricingTitle: 'Preisgestaltung',
    starterPlan: 'Starter',
    starterPrice: '€29',
    starterDesc: 'Perfekt für kleine Projekte',
    premiumPlan: 'Professionell',
    premiumPrice: '€79',
    premiumDesc: 'Für wachsende Unternehmen',
    enterprisePlan: 'Unternehmen',
    enterprisePrice: 'Individuell',
    enterpriseDesc: 'Für spezielle Anforderungen',
    perMonth: '/Monat',
    planFeatures: [
      'Feedback-Widget',
      'Roadmap & Changelog',
      'Grundlegende Analytik',
      'E-Mail-Unterstützung',
      'Benutzerdefinierte Domain'
    ],
    ctaTitle: 'Optimieren Sie Ihr Kundenfeedback Heute',
    ctaDescription: 'Hören Sie mit FeedPanels auf die Stimmen Ihrer Kunden und lassen Sie Ihr Geschäft wachsen',
    faqTitle: 'Häufig Gestellte Fragen',
    faqSubtitle: 'Erhalten Sie Antworten auf Ihre Fragen',
    faqTabProject: 'Projekt',
    faqTabPayment: 'Zahlung',
    // Project FAQs
    faq1Question: 'Wie funktioniert FeedPanels?',
    faq1Answer: 'FeedPanels bietet ein Feedback-Widget, das Sie in Ihre Website integrieren können. Ihre Kunden können ihre Rückmeldungen über dieses Widget senden, und Sie können diese Rückmeldungen in Ihrem Dashboard anzeigen und verwalten.',
    faq2Question: 'Wie lange dauert die Integration?',
    faq2Answer: 'Die Integration ist sehr einfach. Sie müssen nur ein paar Codezeilen hinzufügen und es kann in weniger als 2 Minuten abgeschlossen werden.',
    faq3Question: 'Kann ich das Widget anpassen?',
    faq3Answer: 'Ja, Sie können die Farben, Position und das Erscheinungsbild des Widgets einfach über Ihr Dashboard anpassen.',
    faq4Question: 'Sind meine Daten sicher?',
    faq4Answer: 'Ja, alle Ihre Daten sind mit SSL-Verschlüsselung geschützt und DSGVO-konform. Die Sicherheit Ihrer Daten ist uns sehr wichtig.',
    faq5Question: 'Mit welchen Websites ist es kompatibel?',
    faq5Answer: 'FeedPanels ist mit allen modernen Websites kompatibel. Es funktioniert mit React, Vue, Next.js, Nuxt, WordPress und JavaScript. Sie können es mit einer einzigen Codezeile integrieren.',
    faq6Question: 'Wie verwalte ich Feedback?',
    faq6Answer: 'Sie können Rückmeldungen in den Menüs Feedback und Roadmap anzeigen. Falls gewünscht, können Sie die Position des Feedbacks in der Roadmap aktualisieren, um Ihre Kunden zu informieren.',
    // Payment FAQs
    faqPayment1Question: 'Welche Zahlungsmethoden akzeptieren Sie?',
    faqPayment1Answer: 'Sie können mit Kreditkarte, Debitkarte und PayPal bezahlen. Alle Zahlungen werden sicher verarbeitet.',
    faqPayment2Question: 'Gibt es eine kostenlose Testversion?',
    faqPayment2Answer: 'Ja, nach der Anmeldung erhalten Sie eine 14-tägige kostenlose Testversion. Sie können während dieser Zeit alle Funktionen testen.',
    faqPayment3Question: 'Wie funktioniert die Preisgestaltung?',
    faqPayment3Answer: 'Wir bieten monatliche oder jährliche Abonnementoptionen. Bei jährlichen Zahlungen sparen Sie 20%. Sie können Ihren Plan jederzeit ändern oder kündigen.',
    faqPayment4Question: 'Kann ich kündigen?',
    faqPayment4Answer: 'Ja, Sie können Ihr Abonnement jederzeit kündigen. Die Kündigung tritt sofort in Kraft und Sie werden ab dem nächsten Abrechnungszeitraum nicht mehr belastet.',
    faqPayment5Question: 'Kann ich auf einen gewünschten Plan upgraden?',
    faqPayment5Answer: 'Ja, Sie können sofort auf jeden gewünschten Plan upgraden.',
    faqPayment6Question: 'Sind die Preise inklusive Steuern?',
    faqPayment6Answer: 'Ja, unsere Preise sind inklusive Steuern.',
    testimonial1Name: 'Hans M.',
    testimonial1Company: 'Tech Startup',
    testimonial1Text: 'FeedPanels hat die Verwaltung von Kundenfeedback unglaublich einfach gemacht. Die Kundenzufriedenheit stieg um 40%.',
    testimonial2Name: 'Anna S.',
    testimonial2Company: 'SaaS-Unternehmen',
    testimonial2Text: 'Die Widget-Integration ist sehr einfach, unser ganzes Team kann Feedback schnell sehen. Fantastisches Tool!',
    testimonial3Name: 'Klaus K.',
    testimonial3Company: 'E-Commerce',
    testimonial3Text: 'Dank Feedback-Analytik beschleunigte sich unser Produktentwicklungsprozess um 50%. Großartig!',
    testimonial4Name: 'Sophie M.',
    testimonial4Company: 'Digitale Agentur',
    testimonial4Text: 'Das Sammeln und Verwalten von Kundenfeedback ist jetzt so einfach. Unser Team und unsere Kunden sind sehr zufrieden!',
    // Cookie Consent
    cookieTitle: 'Cookie-Einstellungen',
    cookieDescription: 'Wir verwenden Cookies, um Ihnen das bestmögliche Erlebnis auf dieser Website zu bieten.',
    cookieDescription2: 'Einige Cookies sind für die Funktionsweise der Website erforderlich, während andere uns helfen, diese Website und die Benutzererfahrung zu verbessern.',
    cookieDescription3: 'Sie können selbst entscheiden, ob Sie Cookies nicht zulassen möchten. Bitte beachten Sie, dass die Funktionalität der Website eingeschränkt sein kann, wenn Sie sie ablehnen.',
    cookieSettings: 'Einstellungen',
    cookieRejectAll: 'Alle Ablehnen',
    cookieAcceptAll: 'Alle Akzeptieren',
    cookieEditTitle: 'Cookie-Einstellungen Bearbeiten',
    cookieEditDescription: 'Wählen Sie aus, welche Cookies Sie zulassen möchten. Notwendige Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.',
    cookieNecessary: 'Notwendige Cookies',
    cookieNecessaryDesc: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.',
    cookieFunctional: 'Funktionale Cookies',
    cookieFunctionalDesc: 'Diese Cookies ermöglichen erweiterte Funktionalität und Personalisierung der Website.',
    cookieAnalytical: 'Analytische Cookies',
    cookieAnalyticalDesc: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren, indem sie Informationen anonym sammeln und melden.',
    cookieMarketing: 'Marketing-Cookies',
    cookieMarketingDesc: 'Diese Cookies werden verwendet, um Besuchern relevante Werbung und Marketingkampagnen auf Websites bereitzustellen.',
    cookieBack: 'Zurück',
    cookieSave: 'Einstellungen Speichern',
    cookiePrivacyPolicy: 'Datenschutzerklärung',
    cookieLegalNotice: 'Rechtliche Hinweise',
    footerText: '© 2025 Erstellt von ZeuZ IT',
    footerDescription: 'Verwalten Sie Ihr Kundenfeedback und entwickeln Sie Ihr Geschäft.',
    footerProduct: 'Produkt',
    footerCompany: 'Unternehmen',
    footerResources: 'Ressourcen',
    footerLegal: 'Rechtliches',
    footerFollowUs: 'Folgen Sie uns',
    footerLinks: {
      features: 'Funktionen',
      pricing: 'Preise',
      blog: 'Blog',
      contact: 'Kontakt',
      privacy: 'Datenschutzrichtlinie',
      terms: 'Nutzungsbedingungen'
    },
    // Contact Form
    contactSection: 'Kontaktieren Sie uns',
    contactSectionDesc: 'Haben Sie Fragen? Schreiben Sie uns und wir antworten schnellstmöglich.',
    yourName: 'Ihr Name',
    yourNamePlaceholder: 'Geben Sie Ihren Namen ein',
    email: 'E-Mail',
    emailPlaceholder: 'E-Mail Adresse',
    subject: 'Betreff',
    subjectPlaceholder: 'Nachrichtenbetreff',
    message: 'Nachricht',
    messagePlaceholder: 'Schreiben Sie Ihre Nachricht hier...',
    sendMessage: 'Nachricht Senden',
    sending: 'Wird gesendet...',
    messageSent: 'Ihre Nachricht wurde erfolgreich versendet!',
    messageFailed: 'Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
    allFieldsRequired: 'Bitte füllen Sie alle Felder aus',
    fieldRequiredName: 'Bitte geben Sie Ihren Namen ein',
    fieldRequiredEmail: 'Bitte geben Sie Ihre E-Mail-Adresse ein',
    fieldRequiredSubject: 'Bitte geben Sie den Betreff ein',
    fieldRequiredMessage: 'Bitte geben Sie Ihre Nachricht ein',
    acceptPrivacyPolicy: 'Ich habe die Datenschutzerklärung gelesen und akzeptiere, dass meine Daten zur Bearbeitung meiner Anfrage verwendet werden.',
    privacyPolicyRequired: 'Sie müssen die Datenschutzerklärung akzeptieren.',
    recaptchaRequired: 'Bitte vervollständigen Sie das reCAPTCHA',
    videoSectionTitle: 'FeedPanels in Aktion',
    videoSectionSubtitle: 'Sehen Sie in wenigen Minuten, wie FeedPanels funktioniert',
    videoSectionLoading: 'Wird geladen...',
    // How It Works Section
    howItWorksTitle: 'Wie funktioniert es?',
    howItWorksSubtitle: 'Starten Sie in nur 4 einfachen Schritten',
    step1Title: 'Registrieren',
    step1Desc: 'Erstellen Sie ein kostenloses Konto und starten Sie in Minuten',
    step2Title: 'Integrationscode erhalten',
    step2Desc: 'Kopieren Sie den Widget-Integrationscode aus Ihrem Dashboard',
    step3Title: 'In Ihr Projekt integrieren',
    step3Desc: 'Fügen Sie das Widget mit einer einzigen Codezeile zu Ihrer Website hinzu',
    step4Title: 'Feedback erhalten',
    step4Desc: 'Sehen Sie das Feedback Ihrer Kunden in Echtzeit',
    featureFeedbackTitle: 'Feedback-Verwaltung',
    featureFeedbackDesc: 'Sammeln, kategorisieren und verwalten Sie alle Kundenfeedbacks an einem Ort. Priorisieren Sie Feedback und teilen Sie es mit Ihrem Team.',
    featureRoadmapTitle: 'Roadmap',
    featureRoadmapDesc: 'Präsentieren Sie geplante Funktionen und Verbesserungen in einer visuellen Roadmap. Ihre Kunden können sehen, was kommt, und sich darauf freuen.',
    featureChangelogTitle: 'Änderungsprotokoll',
    featureChangelogDesc: 'Verfolgen Sie alle Ihre Updates und Verbesserungen. Informieren Sie Ihre Kunden einfach über Änderungen.',
    // Testimonials Section
    testimonialsTitle: 'Was Unsere Kunden Sagen',
    testimonialsSubtitle: 'Einige unserer Tausenden zufriedenen Kunden',
  },
  es: {
    brand: 'FeedPanels',
    tagline: 'Plataforma de Retroalimentación del Cliente',
    heroTitle: 'Gestione los Comentarios de los Clientes',
    heroDescription: 'Recopile, organice y transforme los comentarios de los clientes. Administre todas las voces de los clientes en un solo lugar.',
    heroBadge: 'Gestione los Comentarios de los Clientes',
    heroTrust1: 'Seguro y Confiable',
    heroTrust2: 'Integración Rápida',
    heroTrust3: 'Miles de Clientes Felices',
    getStarted: 'Comenzar Gratis',
    getStartedShort: 'Comenzar',
    signIn: 'Iniciar Sesión',
    signInShort: 'Iniciar',
    heroBadgeShort: 'Gestión de Feedback',
    navFeatures: 'Características',
    navHowItWorks: 'Cómo Funciona',
    navPricing: 'Precios',
    navBlog: 'Blog',
    navFAQ: 'Preguntas Frecuentes',
    featuresTitle: '¿Por Qué FeedPanels?',
    feature1Title: 'Integración Simple',
    feature1Desc: 'Integre el widget de comentarios en su sitio web en solo 2 minutos',
    feature2Title: 'Categorización Inteligente',
    feature2Desc: 'Categorice y priorice automáticamente los comentarios',
    feature3Title: 'Roadmap Transparente',
    feature3Desc: 'Muestre a sus clientes el desarrollo de su producto en tiempo real',
    feature4Title: 'Notas de la Versión',
    feature4Desc: 'Anuncie nuevas funciones y rastree cambios',
    feature5Title: 'Colaboración en Equipo',
    feature5Desc: 'Su equipo completo puede ver y comentar sobre comentarios',
    feature6Title: 'Análisis Avanzado',
    feature6Desc: 'Analice las tendencias de comentarios y tome decisiones basadas en datos',
    pricingTitle: 'Precios',
    starterPlan: 'Inicial',
    starterPrice: '€29',
    starterDesc: 'Perfecto para proyectos pequeños',
    premiumPlan: 'Profesional',
    premiumPrice: '€79',
    premiumDesc: 'Para empresas en crecimiento',
    enterprisePlan: 'Empresa',
    enterprisePrice: 'Personalizado',
    enterpriseDesc: 'Para necesidades especiales',
    perMonth: '/mes',
    planFeatures: [
      'Widget de Comentarios',
      'Roadmap y Changelog',
      'Análisis Básico',
      'Soporte por Correo Electrónico',
      'Dominio Personalizado'
    ],
    ctaTitle: 'Optimice Sus Comentarios de Clientes Hoy',
    ctaDescription: 'Escuche las voces de sus clientes con FeedPanels y haga crecer su negocio',
    faqTitle: 'Preguntas Frecuentes',
    faqSubtitle: 'Obtenga respuestas a sus preguntas',
    faqTabProject: 'Proyecto',
    faqTabPayment: 'Pago',
    // Project FAQs
    faq1Question: '¿Cómo funciona FeedPanels?',
    faq1Answer: 'FeedPanels proporciona un widget de comentarios que puede integrar en su sitio web. Sus clientes pueden enviar sus comentarios a través de este widget, y usted puede ver y administrar estos comentarios en su panel.',
    faq2Question: '¿Cuánto tiempo toma la integración?',
    faq2Answer: 'La integración es muy simple. Solo necesita agregar unas pocas líneas de código y se puede completar en menos de 2 minutos.',
    faq3Question: '¿Puedo personalizar el widget?',
    faq3Answer: 'Sí, puede personalizar fácilmente los colores, la posición y la apariencia del widget desde su panel.',
    faq4Question: '¿Están seguros mis datos?',
    faq4Answer: 'Sí, todos sus datos están protegidos con cifrado SSL y son compatibles con GDPR. La seguridad de sus datos es muy importante para nosotros.',
    faq5Question: '¿Con qué sitios web es compatible?',
    faq5Answer: 'FeedPanels es compatible con todos los sitios web modernos. Funciona con React, Vue, Next.js, Nuxt, WordPress y JavaScript. Puede integrarlo con una sola línea de código.',
    faq6Question: '¿Cómo gestiono los comentarios?',
    faq6Answer: 'Puede ver los comentarios en los menús de comentarios y roadmap. Si lo desea, puede actualizar la posición de los comentarios en el roadmap para mantener informados a sus clientes.',
    // Payment FAQs
    faqPayment1Question: '¿Qué métodos de pago aceptan?',
    faqPayment1Answer: 'Puede pagar con tarjeta de crédito, tarjeta de débito y PayPal. Todos los pagos se procesan de forma segura.',
    faqPayment2Question: '¿Hay una prueba gratuita?',
    faqPayment2Answer: 'Sí, después de registrarse, obtiene un período de prueba gratuito de 14 días. Puede probar todas las funciones durante este tiempo.',
    faqPayment3Question: '¿Cómo funciona el precio?',
    faqPayment3Answer: 'Ofrecemos opciones de suscripción mensual o anual. Los pagos anuales le ahorran un 20%. Puede cambiar o cancelar su plan en cualquier momento.',
    faqPayment4Question: '¿Puedo cancelar?',
    faqPayment4Answer: 'Sí, puede cancelar su suscripción en cualquier momento. La cancelación tiene efecto inmediato y no se le cobrará a partir del próximo período de facturación.',
    faqPayment5Question: '¿Puedo actualizar a un plan deseado?',
    faqPayment5Answer: 'Sí, puede actualizar instantáneamente a cualquier plan que desee.',
    faqPayment6Question: '¿Los precios incluyen impuestos?',
    faqPayment6Answer: 'Sí, nuestros precios incluyen impuestos.',
    testimonial1Name: 'Carlos G.',
    testimonial1Company: 'Startup Tecnológico',
    testimonial1Text: 'FeedPanels hizo que la gestión de comentarios de clientes sea increíblemente fácil. La satisfacción del cliente aumentó un 40%.',
    testimonial2Name: 'María L.',
    testimonial2Company: 'Empresa SaaS',
    testimonial2Text: '¡La integración del widget es muy simple, todo nuestro equipo puede ver comentarios rápidamente. ¡Herramienta increíble!',
    testimonial3Name: 'Diego R.',
    testimonial3Company: 'E-Commerce',
    testimonial3Text: 'Gracias a las analíticas de comentarios, nuestro proceso de desarrollo de productos se aceleró un 50%. ¡Me encanta!',
    testimonial4Name: 'Lucía G.',
    testimonial4Company: 'Agencia Digital',
    testimonial4Text: 'Recopilar y gestionar comentarios de clientes ahora es muy fácil. ¡Nuestro equipo y clientes están muy satisfechos!',
    // Cookie Consent
    cookieTitle: 'Configuración de Cookies',
    cookieDescription: 'Utilizamos cookies para brindarle la mejor experiencia posible en este sitio web.',
    cookieDescription2: 'Algunas cookies son necesarias para el funcionamiento del sitio web, mientras que otras nos ayudan a mejorar este sitio web y la experiencia del usuario.',
    cookieDescription3: 'Puede decidir por sí mismo si no desea permitir cookies. Tenga en cuenta que si las rechaza, la funcionalidad del sitio web puede verse limitada.',
    cookieSettings: 'Configuración',
    cookieRejectAll: 'Rechazar Todo',
    cookieAcceptAll: 'Aceptar Todo',
    cookieEditTitle: 'Editar Configuración de Cookies',
    cookieEditDescription: 'Elija qué cookies desea permitir. Las cookies necesarias son esenciales para las funciones básicas del sitio web y no se pueden desactivar.',
    cookieNecessary: 'Cookies Necesarias',
    cookieNecessaryDesc: 'Estas cookies son esenciales para las funciones básicas del sitio web y no se pueden desactivar.',
    cookieFunctional: 'Cookies Funcionales',
    cookieFunctionalDesc: 'Estas cookies permiten funcionalidad avanzada y personalización del sitio web.',
    cookieAnalytical: 'Cookies Analíticas',
    cookieAnalyticalDesc: 'Estas cookies nos ayudan a comprender cómo los visitantes interactúan con el sitio web recopilando e informando información de forma anónima.',
    cookieMarketing: 'Cookies de Marketing',
    cookieMarketingDesc: 'Estas cookies se utilizan para proporcionar anuncios y campañas de marketing relevantes a los visitantes en sitios web.',
    cookieBack: 'Atrás',
    cookieSave: 'Guardar Configuración',
    cookiePrivacyPolicy: 'Política de Privacidad',
    cookieLegalNotice: 'Aviso Legal',
    footerText: '© 2025 Creado por ZeuZ IT',
    footerDescription: 'Gestione los comentarios de sus clientes y haga crecer su negocio.',
    footerProduct: 'Producto',
    footerCompany: 'Empresa',
    footerResources: 'Recursos',
    footerLegal: 'Legal',
    footerFollowUs: 'Síguenos',
    footerLinks: {
      features: 'Características',
      pricing: 'Precios',
      blog: 'Blog',
      contact: 'Contacto',
      privacy: 'Política de Privacidad',
      terms: 'Términos de Servicio'
    },
    // Contact Form
    contactSection: 'Contáctenos',
    contactSectionDesc: '¿Tiene preguntas? Escribanos y le responderemos lo antes posible.',
    yourName: 'Su Nombre',
    yourNamePlaceholder: 'Ingrese su nombre',
    email: 'Correo Electrónico',
    emailPlaceholder: 'email@example.com',
    subject: 'Asunto',
    subjectPlaceholder: 'Asunto del mensaje',
    message: 'Mensaje',
    messagePlaceholder: 'Escriba su mensaje aquí...',
    sendMessage: 'Enviar Mensaje',
    sending: 'Enviando...',
    messageSent: '¡Su mensaje se ha enviado correctamente!',
    messageFailed: 'El mensaje no se pudo enviar. Por favor, inténtelo de nuevo.',
    allFieldsRequired: 'Por favor complete todos los campos',
    fieldRequiredName: 'Por favor ingrese su nombre',
    fieldRequiredEmail: 'Por favor ingrese su dirección de correo electrónico',
    fieldRequiredSubject: 'Por favor ingrese el asunto',
    fieldRequiredMessage: 'Por favor ingrese su mensaje',
    acceptPrivacyPolicy: 'He leído la Política de Privacidad y acepto que mis datos se utilicen para procesar mi solicitud.',
    privacyPolicyRequired: 'Debe aceptar la Política de Privacidad.',
    recaptchaRequired: 'Por favor complete el reCAPTCHA',
    videoSectionTitle: 'Descubre FeedPanels',
    videoSectionSubtitle: 'Descubre cómo funciona FeedPanels en minutos',
    videoSectionLoading: 'Cargando...',
    // How It Works Section
    howItWorksTitle: '¿Cómo Funciona?',
    howItWorksSubtitle: 'Comience en solo 4 pasos simples',
    step1Title: 'Registrarse',
    step1Desc: 'Cree una cuenta gratuita y comience en minutos',
    step2Title: 'Obtener Código de Integración',
    step2Desc: 'Copie el código de integración del widget desde su panel',
    step3Title: 'Integrar a Su Proyecto',
    step3Desc: 'Agregue el widget a su sitio web con una sola línea de código',
    step4Title: 'Recibir Comentarios',
    step4Desc: 'Vea los comentarios de sus clientes en tiempo real',
    featureFeedbackTitle: 'Gestión de Comentarios',
    featureFeedbackDesc: 'Recopile, categorice y gestione todos los comentarios de los clientes en un solo lugar. Priorice los comentarios y compártalos con su equipo.',
    featureRoadmapTitle: 'Hoja de Ruta',
    featureRoadmapDesc: 'Presente las funciones y mejoras planificadas en una hoja de ruta visual. Sus clientes pueden ver lo que viene y emocionarse.',
    featureChangelogTitle: 'Registro de Cambios',
    featureChangelogDesc: 'Rastree todas sus actualizaciones y mejoras. Informe fácilmente a sus clientes sobre lo que ha cambiado.',
    // Testimonials Section
    testimonialsTitle: 'Lo Que Dicen Nuestros Clientes',
    testimonialsSubtitle: 'Algunos de nuestros miles de clientes satisfechos',
  },
  fr: {
    brand: 'FeedPanels',
    tagline: 'Plateforme de Retours Clients',
    heroTitle: 'Gérez les Retours des Clients',
    heroDescription: 'Collectez, organisez et transformez les retours des clients. Gérez toutes les voix des clients en un seul endroit.',
    heroBadge: 'Gérez les Retours des Clients',
    heroTrust1: 'Sécurisé et Fiable',
    heroTrust2: 'Intégration Rapide',
    heroTrust3: 'Des Milliers de Clients Heureux',
    getStarted: 'Commencer Gratuitement',
    getStartedShort: 'Commencer',
    signIn: 'Se Connecter',
    signInShort: 'Connexion',
    heroBadgeShort: 'Gestion des Commentaires',
    navFeatures: 'Fonctionnalités',
    navHowItWorks: 'Comment Ça Marche',
    navPricing: 'Tarification',
    navBlog: 'Blog',
    navFAQ: 'FAQ',
    featuresTitle: 'Pourquoi FeedPanels?',
    feature1Title: 'Intégration Simple',
    feature1Desc: 'Intégrez le widget de retours à votre site web en seulement 2 minutes',
    feature2Title: 'Catégorisation Intelligente',
    feature2Desc: 'Catégorisez et priorisez automatiquement les retours',
    feature3Title: 'Feuille de Route Transparente',
    feature3Desc: 'Montrez à vos clients le développement de votre produit en temps réel',
    feature4Title: 'Notes de Version',
    feature4Desc: 'Annoncez les nouvelles fonctionnalités et suivez les modifications',
    feature5Title: 'Collaboration d\'Équipe',
    feature5Desc: 'Votre équipe entière peut visualiser et commenter les retours',
    feature6Title: 'Analyse Avancée',
    feature6Desc: 'Analysez les tendances des retours et prenez des décisions basées sur les données',
    pricingTitle: 'Tarification',
    starterPlan: 'Démarrage',
    starterPrice: '€29',
    starterDesc: 'Parfait pour les petits projets',
    premiumPlan: 'Professionnel',
    premiumPrice: '€79',
    premiumDesc: 'Pour les entreprises en croissance',
    enterprisePlan: 'Entreprise',
    enterprisePrice: 'Personnalisé',
    enterpriseDesc: 'Pour les besoins particuliers',
    perMonth: '/mois',
    planFeatures: [
      'Widget de Retours',
      'Feuille de Route & Changelog',
      'Analyse de Base',
      'Support par Email',
      'Domaine Personnalisé'
    ],
    ctaTitle: 'Optimisez Vos Retours Clients Aujourd\'hui',
    ctaDescription: 'Écoutez les voix de vos clients avec FeedPanels et développez votre entreprise',
    faqTitle: 'Questions Fréquemment Posées',
    faqSubtitle: 'Obtenez des réponses à vos questions',
    faqTabProject: 'Projet',
    faqTabPayment: 'Paiement',
    // Project FAQs
    faq1Question: 'Comment fonctionne FeedPanels?',
    faq1Answer: 'FeedPanels fournit un widget de retours que vous pouvez intégrer dans votre site web. Vos clients peuvent soumettre leurs retours via ce widget, et vous pouvez visualiser et gérer ces retours dans votre tableau de bord.',
    faq2Question: 'Combien de temps prend l\'intégration?',
    faq2Answer: 'L\'intégration est très simple. Vous devez simplement ajouter quelques lignes de code et cela peut être complété en moins de 2 minutes.',
    faq3Question: 'Puis-je personnaliser le widget?',
    faq3Answer: 'Oui, vous pouvez facilement personnaliser les couleurs, la position et l\'apparence du widget depuis votre tableau de bord.',
    faq4Question: 'Mes données sont-elles sécurisées?',
    faq4Answer: 'Oui, toutes vos données sont protégées par un chiffrement SSL et sont conformes au RGPD. La sécurité de vos données est très importante pour nous.',
    faq5Question: 'Avec quels sites web est-il compatible?',
    faq5Answer: 'FeedPanels est compatible avec tous les sites web modernes. Il fonctionne avec React, Vue, Next.js, Nuxt, WordPress et JavaScript. Vous pouvez l\'intégrer avec une seule ligne de code.',
    faq6Question: 'Comment gérer les retours?',
    faq6Answer: 'Vous pouvez voir les retours dans les menus retours et roadmap. Si vous le souhaitez, vous pouvez mettre à jour la position du feedback sur la roadmap pour tenir vos clients informés.',
    // Payment FAQs
    faqPayment1Question: 'Quels modes de paiement acceptez-vous?',
    faqPayment1Answer: 'Vous pouvez payer par carte de crédit, carte de débit et PayPal. Tous les paiements sont traités en toute sécurité.',
    faqPayment2Question: 'Y a-t-il un essai gratuit?',
    faqPayment2Answer: 'Oui, après votre inscription, vous bénéficiez d\'une période d\'essai gratuite de 14 jours. Vous pouvez tester toutes les fonctionnalités pendant cette période.',
    faqPayment3Question: 'Comment fonctionne la tarification?',
    faqPayment3Answer: 'Nous proposons des options d\'abonnement mensuel ou annuel. Les paiements annuels vous font économiser 20%. Vous pouvez modifier ou annuler votre plan à tout moment.',
    faqPayment4Question: 'Puis-je annuler?',
    faqPayment4Answer: 'Oui, vous pouvez annuler votre abonnement à tout moment. L\'annulation prend effet immédiatement et vous ne serez plus facturé à partir de la prochaine période de facturation.',
    faqPayment5Question: 'Puis-je passer à un plan désiré?',
    faqPayment5Answer: 'Oui, vous pouvez passer instantanément à n\'importe quel plan que vous souhaitez.',
    faqPayment6Question: 'Les prix incluent-ils les taxes?',
    faqPayment6Answer: 'Oui, nos prix incluent les taxes.',
    testimonial1Name: 'Pierre D.',
    testimonial1Company: 'Startup Tech',
    testimonial1Text: 'FeedPanels a rendu la gestion des retours clients incroyablement facile. La satisfaction client a augmenté de 40%.',
    testimonial2Name: 'Sophie M.',
    testimonial2Company: 'Entreprise SaaS',
    testimonial2Text: 'L\'intégration du widget est très simple, toute notre équipe peut voir les retours rapidement. Outil remarquable!',
    testimonial3Name: 'Laurent B.',
    testimonial3Company: 'E-Commerce',
    testimonial3Text: 'Grâce à l\'analyse des retours, notre processus de développement de produit s\'est accéléré de 50%. Excellent!',
    testimonial4Name: 'Camille F.',
    testimonial4Company: 'Agence Digitale',
    testimonial4Text: 'Collecter et gérer les retours clients est maintenant si facile. Notre équipe et nos clients sont très satisfaits!',
    // Cookie Consent
    cookieTitle: 'Paramètres des Cookies',
    cookieDescription: 'Nous utilisons des cookies pour vous offrir la meilleure expérience possible sur ce site web.',
    cookieDescription2: 'Certains cookies sont nécessaires au fonctionnement du site web, tandis que d\'autres nous aident à améliorer ce site web et l\'expérience utilisateur.',
    cookieDescription3: 'Vous pouvez décider vous-même si vous ne souhaitez pas autoriser les cookies. Veuillez noter que si vous les refusez, la fonctionnalité du site web peut être limitée.',
    cookieSettings: 'Paramètres',
    cookieRejectAll: 'Tout Refuser',
    cookieAcceptAll: 'Tout Accepter',
    cookieEditTitle: 'Modifier les Paramètres des Cookies',
    cookieEditDescription: 'Choisissez les cookies que vous souhaitez autoriser. Les cookies nécessaires sont essentiels pour les fonctions de base du site web et ne peuvent pas être désactivés.',
    cookieNecessary: 'Cookies Nécessaires',
    cookieNecessaryDesc: 'Ces cookies sont essentiels pour les fonctions de base du site web et ne peuvent pas être désactivés.',
    cookieFunctional: 'Cookies Fonctionnels',
    cookieFunctionalDesc: 'Ces cookies permettent une fonctionnalité avancée et la personnalisation du site web.',
    cookieAnalytical: 'Cookies Analytiques',
    cookieAnalyticalDesc: 'Ces cookies nous aident à comprendre comment les visiteurs interagissent avec le site web en collectant et en rapportant des informations de manière anonyme.',
    cookieMarketing: 'Cookies Marketing',
    cookieMarketingDesc: 'Ces cookies sont utilisés pour fournir des publicités et des campagnes marketing pertinentes aux visiteurs sur les sites web.',
    cookieBack: 'Retour',
    cookieSave: 'Enregistrer les Paramètres',
    cookiePrivacyPolicy: 'Politique de Confidentialité',
    cookieLegalNotice: 'Avis Légal',
    footerText: '© 2025 Créé par ZeuZ IT',
    footerDescription: 'Gérez les retours de vos clients et développez votre entreprise.',
    footerProduct: 'Produit',
    footerCompany: 'Entreprise',
    footerResources: 'Ressources',
    footerLegal: 'Légal',
    footerFollowUs: 'Suivez-nous',
    footerLinks: {
      features: 'Fonctionnalités',
      pricing: 'Tarification',
      blog: 'Blog',
      contact: 'Contact',
      privacy: 'Politique de Confidentialité',
      terms: 'Conditions d\'Utilisation'
    },
    // Contact Form
    contactSection: 'Nous Contacter',
    contactSectionDesc: 'Vous avez des questions ? Écrivez-nous et nous vous répondrons dès que possible.',
    yourName: 'Votre Nom',
    yourNamePlaceholder: 'Entrez votre nom',
    email: 'Email',
    emailPlaceholder: 'email@example.com',
    subject: 'Sujet',
    subjectPlaceholder: 'Sujet du message',
    message: 'Message',
    messagePlaceholder: 'Écrivez votre message ici...',
    sendMessage: 'Envoyer le Message',
    sending: 'Envoi en cours...',
    messageSent: 'Votre message a été envoyé avec succès!',
    messageFailed: 'Le message n\'a pas pu être envoyé. Veuillez réessayer.',
    allFieldsRequired: 'Veuillez remplir tous les champs',
    fieldRequiredName: 'Veuillez entrer votre nom',
    fieldRequiredEmail: 'Veuillez entrer votre adresse e-mail',
    fieldRequiredSubject: 'Veuillez entrer le sujet',
    fieldRequiredMessage: 'Veuillez entrer votre message',
    acceptPrivacyPolicy: 'J\'ai lu la Politique de Confidentialité et j\'accepte que mes données soient utilisées pour traiter ma demande.',
    privacyPolicyRequired: 'Vous devez accepter la Politique de Confidentialité.',
    recaptchaRequired: 'Veuillez compléter le reCAPTCHA',
    videoSectionTitle: 'Découvrez FeedPanels',
    videoSectionSubtitle: 'Découvrez comment FeedPanels fonctionne en quelques minutes',
    videoSectionLoading: 'Chargement...',
    // How It Works Section
    howItWorksTitle: 'Comment Ça Marche?',
    howItWorksSubtitle: 'Commencez en seulement 4 étapes simples',
    step1Title: 'S\'inscrire',
    step1Desc: 'Créez un compte gratuit et commencez en quelques minutes',
    step2Title: 'Obtenir le Code d\'Intégration',
    step2Desc: 'Copiez le code d\'intégration du widget depuis votre tableau de bord',
    step3Title: 'Intégrer à Votre Projet',
    step3Desc: 'Ajoutez le widget à votre site web avec une seule ligne de code',
    step4Title: 'Recevoir les Retours',
    step4Desc: 'Visualisez les retours de vos clients en temps réel',
    featureFeedbackTitle: 'Gestion des Commentaires',
    featureFeedbackDesc: 'Collectez, catégorisez et gérez tous les commentaires des clients en un seul endroit. Priorisez les commentaires et partagez-les avec votre équipe.',
    featureRoadmapTitle: 'Feuille de Route',
    featureRoadmapDesc: 'Présentez les fonctionnalités et améliorations prévues dans une feuille de route visuelle. Vos clients peuvent voir ce qui arrive et s\'enthousiasmer.',
    featureChangelogTitle: 'Journal des Modifications',
    featureChangelogDesc: 'Suivez toutes vos mises à jour et améliorations. Informez facilement vos clients des changements.',
    // Testimonials Section
    testimonialsTitle: 'Ce Que Disent Nos Clients',
    testimonialsSubtitle: 'Quelques-uns de nos milliers de clients satisfaits',
  },
};

// Search snippet per language. English is the canonical wording already indexed and
// already baked into the static HTML (scripts/seo-meta.json) — the others are
// translations of it, not new claims.
const SEO_META = {
  en: {
    title: 'FeedPanels - Customer Feedback Management Platform',
    description:
      'Collect, analyze and act on customer feedback. FeedPanels helps you understand what your customers want.',
  },
  tr: {
    title: 'FeedPanels - Müşteri Geri Bildirim Yönetim Platformu',
    description:
      'Müşteri geri bildirimlerini toplayın, analiz edin ve harekete geçin. FeedPanels müşterilerinizin ne istediğini anlamanıza yardımcı olur.',
  },
  de: {
    title: 'FeedPanels - Plattform für Kundenfeedback-Management',
    description:
      'Sammeln, analysieren und nutzen Sie Kundenfeedback. FeedPanels hilft Ihnen zu verstehen, was Ihre Kunden wirklich wollen.',
  },
  es: {
    title: 'FeedPanels - Plataforma de Gestión de Comentarios de Clientes',
    description:
      'Recopile, analice y actúe sobre los comentarios de sus clientes. FeedPanels le ayuda a entender qué quieren sus clientes.',
  },
  fr: {
    title: 'FeedPanels - Plateforme de Gestion des Retours Clients',
    description:
      'Collectez, analysez et exploitez les retours de vos clients. FeedPanels vous aide à comprendre ce que veulent vos clients.',
  },
};

// Every language needs its own URL for hreflang to mean anything. "/" is English
// and doubles as x-default; the rest hang off ?lang= (same pattern the blog uses).
const LANDING_URL = 'https://feedpanels.com/';
const langUrl = (lang) => (lang === 'en' ? LANDING_URL : `${LANDING_URL}?lang=${lang}`);

// Brand profiles that are already linked from this page (badges, footer, noscript nav).
// Kept here so Organization.sameAs stays in sync with what the page actually links to.
const SAME_AS_PROFILES = [
  'https://www.producthunt.com/products/feedpanels-com',
  'https://www.uneed.best/tool/feed-panels',
  'https://www.youtube.com/@feedpanels',
  'https://www.instagram.com/feedpanels/',
  'https://www.facebook.com/profile.php?id=61587291293117',
  'https://zeuz-it.de/',
];

export const LandingPage = () => {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const t = LandingPageTranslations[language] || LandingPageTranslations.en;
  const seo = SEO_META[language] || SEO_META.en;

  // FAQPage schema built from the same translations the visible FAQ section renders,
  // so the markup can never drift from what users actually see.
  const faqJsonLd = {
    '@type': 'FAQPage',
    '@id': 'https://feedpanels.com/#faq',
    mainEntity: [1, 2, 3, 4, 5, 6]
      .map((n) => ({ q: t[`faq${n}Question`], a: t[`faq${n}Answer`] }))
      .filter(({ q, a }) => q && a)
      .map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
  };

  // Helper function to get legal document route URL
  const getLegalDocumentUrl = (type) => {
    return `/legal/${type}`;
  };

  const languages = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
  ];

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const recaptchaWidgetId = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navbarRef = useRef(null);
  const lastScrollY = useRef(0);
  const videoSectionRef = useRef(null);
  const [videoInView, setVideoInView] = useState(false);
  // reCAPTCHA is ~1.2 MB of third-party JS. It used to load on every page view from
  // index.html; now it loads only when the contact form comes into range.
  const contactSectionRef = useRef(null);
  const [contactInView, setContactInView] = useState(false);

  // Navbar scroll behavior - show on scroll up, hide on scroll down
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const navbar = navbarRef.current;

          if (!navbar) return;

          if (currentScrollY < 10) {
            // At the top - show navbar
            navbar.style.transform = 'translateY(0)';
            navbar.style.opacity = '1';
            navbar.style.pointerEvents = 'auto';
          } else if (currentScrollY > lastScrollY.current) {
            // Scrolling down - hide navbar
            navbar.style.transform = 'translateY(-100%)';
            navbar.style.opacity = '0';
            navbar.style.pointerEvents = 'none';
          } else {
            // Scrolling up - show navbar
            navbar.style.transform = 'translateY(0)';
            navbar.style.opacity = '1';
            navbar.style.pointerEvents = 'auto';
          }

          lastScrollY.current = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lazy load video: only load iframe when video section is in view
  useEffect(() => {
    const ref = videoSectionRef.current;
    if (!ref) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVideoInView(true);
      },
      { rootMargin: '100px', threshold: 0.1 }
    );
    observer.observe(ref);
    return () => observer.disconnect();
  }, []);

  // Lazy load reCAPTCHA: arm well before the form is reachable (800px) so the
  // widget is ready by the time the visitor can actually type into it.
  useEffect(() => {
    const ref = contactSectionRef.current;
    if (!ref) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setContactInView(true);
      },
      { rootMargin: '800px' }
    );
    observer.observe(ref);
    return () => observer.disconnect();
  }, []);

  // Initialize reCAPTCHA widget
  useEffect(() => {
    if (!contactInView) return;

    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    if (!siteKey) {
      console.warn('reCAPTCHA Site Key not found. Please set REACT_APP_RECAPTCHA_SITE_KEY environment variable.');
      // Show error message in container
      if (recaptchaRef.current) {
        recaptchaRef.current.innerHTML = '<p class="text-red-500 text-sm">reCAPTCHA Site Key bulunamadı. Lütfen REACT_APP_RECAPTCHA_SITE_KEY environment variable\'ını ayarlayın.</p>';
      }
      return;
    }

    // Map language code to reCAPTCHA language code
    const getRecaptchaLanguage = (lang) => {
      const langMap = {
        'tr': 'tr',
        'en': 'en',
        'de': 'de',
        'es': 'es',
        'fr': 'fr'
      };
      return langMap[lang] || 'en';
    };

    const loadRecaptcha = () => {
      if (!recaptchaRef.current) {
        console.warn('reCAPTCHA container not found');
        return;
      }

      if (window.grecaptcha && window.grecaptcha.render) {
        try {
          // Get current language for reCAPTCHA
          const recaptchaLang = getRecaptchaLanguage(language);

          // Render reCAPTCHA widget with language parameter
          const widgetId = window.grecaptcha.render(recaptchaRef.current, {
            sitekey: siteKey,
            hl: recaptchaLang, // Language parameter for reCAPTCHA
            callback: (token) => {
              setRecaptchaToken(token);
            },
            'expired-callback': () => {
              setRecaptchaToken(null);
            },
            'error-callback': () => {
              console.error('reCAPTCHA error occurred');
              setRecaptchaToken(null);
            }
          });
          recaptchaWidgetId.current = widgetId;
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error);
          if (recaptchaRef.current) {
            recaptchaRef.current.innerHTML = '<p class="text-red-500 text-sm">reCAPTCHA yüklenirken hata oluştu.</p>';
          }
        }
      } else {
        console.warn('window.grecaptcha not available');
      }
    };

    // Note: Key prop on the reCAPTCHA container element ensures that when language changes,
    // React unmounts the old element and mounts a new one, which clears Google's internal registry.
    // This allows fresh rendering of the widget with the new language.

    // The script is no longer in index.html — inject it on first need. Guarded by id
    // so a language change (which re-runs this effect) doesn't add a second copy.
    const RECAPTCHA_SCRIPT_ID = 'recaptcha-api-script';
    if (!document.getElementById(RECAPTCHA_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    // Check if grecaptcha is already loaded
    let checkInterval;
    let timeoutId;
    if (window.grecaptcha && window.grecaptcha.render) {
      loadRecaptcha();
    } else {
      // Wait for script to load
      checkInterval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.render) {
          clearInterval(checkInterval);
          loadRecaptcha();
        }
      }, 100);

      // Timeout after 10 seconds
      timeoutId = setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.grecaptcha || !window.grecaptcha.render) {
          console.error('reCAPTCHA script failed to load after 10 seconds');
          if (recaptchaRef.current) {
            recaptchaRef.current.innerHTML = '<p class="text-red-500 text-sm">reCAPTCHA script yüklenemedi. Lütfen sayfayı yenileyin.</p>';
          }
        }
      }, 10000);
    }

    return () => {
      // Cleanup on language change or unmount
      // Key change causes element to unmount, clearing Google's internal registry
      clearInterval(checkInterval);
      clearTimeout(timeoutId);
      setRecaptchaToken(null);
      recaptchaWidgetId.current = null;
    };
  }, [language, contactInView]); // Re-render on language change; wait until the form is near

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleInvalid = (e) => {
    e.preventDefault();
    const field = e.target;
    
    // Show custom validation message based on field
    if (field.id === 'name') {
      toast.error(t.fieldRequiredName);
    } else if (field.id === 'email') {
      toast.error(t.fieldRequiredEmail);
    } else if (field.id === 'subject') {
      toast.error(t.fieldRequiredSubject);
    } else if (field.id === 'message') {
      toast.error(t.fieldRequiredMessage);
    } else {
      toast.error(t.allFieldsRequired);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate each field individually
    if (!formData.name) {
      toast.error(t.fieldRequiredName);
      return;
    }
    if (!formData.email) {
      toast.error(t.fieldRequiredEmail);
      return;
    }
    if (!formData.subject) {
      toast.error(t.fieldRequiredSubject);
      return;
    }
    if (!formData.message) {
      toast.error(t.fieldRequiredMessage);
      return;
    }

    if (!acceptPrivacy) {
      toast.error(t.privacyPolicyRequired);
      return;
    }

    if (!recaptchaToken) {
      toast.error(t.recaptchaRequired);
      return;
    }

    setLoading(true);

    try {
      await api.post('/contact', {
        ...formData,
        recaptcha_token: recaptchaToken
      });
      toast.success(t.messageSent);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setAcceptPrivacy(false);
      setRecaptchaToken(null);
      // Reset reCAPTCHA widget
      if (window.grecaptcha && recaptchaWidgetId.current !== null) {
        try {
          window.grecaptcha.reset(recaptchaWidgetId.current);
        } catch (error) {
          console.error('Error resetting reCAPTCHA:', error);
        }
      }
    } catch (error) {
      console.error('Failed to send contact message:', error);
      toast.error(error.response?.data?.detail || t.messageFailed);
      // Reset reCAPTCHA on error
      setRecaptchaToken(null);
      if (window.grecaptcha && recaptchaWidgetId.current !== null) {
        try {
          window.grecaptcha.reset(recaptchaWidgetId.current);
        } catch (resetError) {
          console.error('Error resetting reCAPTCHA:', resetError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { 
      icon: Zap, 
      title: t.feature1Title, 
      desc: t.feature1Desc,
      gradient: 'from-yellow-400 via-orange-500 to-red-500',
      bgGradient: 'from-yellow-50 to-orange-50'
    },
    { 
      icon: BarChart3, 
      title: t.feature2Title, 
      desc: t.feature2Desc,
      gradient: 'from-blue-500 via-cyan-500 to-teal-500',
      bgGradient: 'from-blue-50 to-cyan-50'
    },
    { 
      icon: Globe, 
      title: t.feature3Title, 
      desc: t.feature3Desc,
      gradient: 'from-green-500 via-emerald-500 to-teal-500',
      bgGradient: 'from-green-50 to-emerald-50'
    },
    { 
      icon: Star, 
      title: t.feature4Title, 
      desc: t.feature4Desc,
      gradient: 'from-purple-500 via-pink-500 to-rose-500',
      bgGradient: 'from-purple-50 to-pink-50'
    },
    { 
      icon: Users, 
      title: t.feature5Title, 
      desc: t.feature5Desc,
      gradient: 'from-indigo-500 via-blue-500 to-cyan-500',
      bgGradient: 'from-indigo-50 to-blue-50'
    },
    { 
      icon: BarChart3, 
      title: t.feature6Title, 
      desc: t.feature6Desc,
      gradient: 'from-cyan-500 via-teal-500 to-green-500',
      bgGradient: 'from-cyan-50 to-teal-50'
    },
  ];

  const projectFaqs = [
    {
      question: t.faq1Question,
      answer: t.faq1Answer,
    },
    {
      question: t.faq2Question,
      answer: t.faq2Answer,
    },
    {
      question: t.faq3Question,
      answer: t.faq3Answer,
    },
    {
      question: t.faq4Question,
      answer: t.faq4Answer,
    },
    {
      question: t.faq5Question,
      answer: t.faq5Answer,
    },
    {
      question: t.faq6Question,
      answer: t.faq6Answer,
    },
  ];

  const paymentFaqs = [
    {
      question: t.faqPayment1Question,
      answer: t.faqPayment1Answer,
    },
    {
      question: t.faqPayment2Question,
      answer: t.faqPayment2Answer,
    },
    {
      question: t.faqPayment3Question,
      answer: t.faqPayment3Answer,
    },
    {
      question: t.faqPayment4Question,
      answer: t.faqPayment4Answer,
    },
    {
      question: t.faqPayment5Question,
      answer: t.faqPayment5Answer,
    },
    {
      question: t.faqPayment6Question,
      answer: t.faqPayment6Answer,
    },
  ];

  // Step Component
  const StepCard = ({ step, index }) => {
    const stepRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setIsVisible(true);
            }, index * 200);
          }
        },
        { threshold: 0.1 }
      );

      if (stepRef.current) {
        observer.observe(stepRef.current);
      }

      return () => {
        if (stepRef.current) {
          observer.unobserve(stepRef.current);
        }
      };
    }, [index]);

    const Icon = step.icon;

    return (
      <div
        ref={stepRef}
        className={`relative z-10 transition-all duration-700 ease-out ${
          isVisible
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-10 scale-95'
        }`}
      >
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg dark:shadow-gray-900/50 hover:shadow-xl dark:hover:shadow-gray-900/70 transition-all duration-300 border border-gray-100 dark:border-gray-700 h-full flex flex-col items-center text-center group hover:-translate-y-2">
          {/* Step Number */}
          <div className={`absolute -top-4 sm:-top-6 left-1/2 transform -translate-x-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg transition-transform duration-300 group-hover:scale-110`}>
            {index + 1}
          </div>

          {/* Icon */}
          <div className={`mt-2 sm:mt-4 mb-4 sm:mb-6 w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="w-6 h-6 sm:w-8 sm:h-8 transition-transform duration-300 group-hover:rotate-6" />
          </div>

          {/* Content */}
          <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 transition-colors duration-300 group-hover:text-cyan-600 dark:group-hover:text-cyan-400">
            {step.title}
          </h4>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 flex-grow">
            {step.desc}
          </p>

          {/* Arrow for mobile */}
          {index < 3 && (
            <div className="sm:hidden mt-4 text-cyan-500 dark:text-cyan-400 animate-pulse">
              <ArrowRight className="w-5 h-5" />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Cookie Consent Component
  const CookieConsent = () => {
    // Local state for cookie consent
    const [showCookieModal, setShowCookieModal] = useState(false);
    const [showCookieSettings, setShowCookieSettings] = useState(false);
    const [cookiePreferences, setCookiePreferences] = useState({
      necessary: true, // Always true, cannot be disabled
      functional: false,
      analytical: false,
      marketing: false
    });

    // Check if user has already made a choice
    useEffect(() => {
      const cookieConsent = localStorage.getItem('cookieConsent');
      if (!cookieConsent) {
        setShowCookieModal(true);
      } else {
        const preferences = JSON.parse(cookieConsent);
        setCookiePreferences(preferences);
      }
    }, []);

    // Disable animations when modal opens/closes
    useEffect(() => {
      if (showCookieModal) {
        // Modal açılıyor - animasyonları durdur
        ScrollTrigger.disable();
        gsap.globalTimeline.pause();
        document.body.style.overflow = 'hidden';
      } else {
        // Modal kapatılıyor - animasyonları devam ettir
        document.body.style.overflow = 'auto';
        gsap.globalTimeline.resume();
        ScrollTrigger.enable();
      }

      return () => {
        // Cleanup: Modal kapatılırken kesin olarak enable et
        document.body.style.overflow = 'auto';
        gsap.globalTimeline.resume();
        ScrollTrigger.enable();
      };
    }, [showCookieModal]);

    const handleAcceptAll = useCallback(() => {
      const allAccepted = {
        necessary: true,
        functional: true,
        analytical: true,
        marketing: true
      };
      setCookiePreferences(allAccepted);
      localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
      // Lets analytics start (or stay off) without a reload — see
      // components/PostHogProvider.jsx, which listens for this.
      window.dispatchEvent(new Event(CONSENT_EVENT));
      setShowCookieModal(false);
    }, []);

    const handleRejectAll = useCallback(() => {
      const onlyNecessary = {
        necessary: true,
        functional: false,
        analytical: false,
        marketing: false
      };
      setCookiePreferences(onlyNecessary);
      localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
      // Lets analytics start (or stay off) without a reload — see
      // components/PostHogProvider.jsx, which listens for this.
      window.dispatchEvent(new Event(CONSENT_EVENT));
      setShowCookieModal(false);
    }, []);

    const handleSaveSettings = useCallback(() => {
      localStorage.setItem('cookieConsent', JSON.stringify(cookiePreferences));
      // Lets analytics start (or stay off) without a reload — see
      // components/PostHogProvider.jsx, which listens for this.
      window.dispatchEvent(new Event(CONSENT_EVENT));
      setShowCookieSettings(false);
      setShowCookieModal(false);
    }, [cookiePreferences]);

    const toggleCookie = useCallback((type) => {
      if (type === 'necessary') return; // Cannot disable necessary cookies
      setCookiePreferences(prev => ({
        ...prev,
        [type]: !prev[type]
      }));
    }, []);

    const handleOpenCookieSettings = () => {
      setShowCookieSettings(true);
      setShowCookieModal(true);
    };

    return (
      <>
        {/* Cookie Settings Button - Fixed bottom left */}
        <button
          onClick={handleOpenCookieSettings}
          className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-cyan-600 hover:bg-cyan-700 dark:bg-cyan-500 dark:hover:bg-cyan-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
          aria-label="Cookie Settings"
          title={t.cookieSettings}
        >
          <Cookie className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>

        {/* Cookie Modal */}
        {showCookieModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {!showCookieSettings ? (
            // Main Cookie Modal
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                <Cookie className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t.cookieTitle}
                </h2>
              </div>
              
              <div className="space-y-3 mb-6 text-gray-700 dark:text-gray-300">
                <p>{t.cookieDescription}</p>
                <p>{t.cookieDescription2}</p>
                <p>{t.cookieDescription3}</p>
              </div>

              <div className="flex flex-wrap gap-2 mb-4 text-sm">
                <Link
                  to={getLegalDocumentUrl('privacy')}
                  onClick={() => setShowCookieModal(false)}
                  className="text-cyan-600 dark:text-cyan-400 hover:underline"
                >
                  {t.cookiePrivacyPolicy}
                </Link>
                <span className="text-gray-400">•</span>
                <Link
                  to={getLegalDocumentUrl('legal')}
                  onClick={() => setShowCookieModal(false)}
                  className="text-cyan-600 dark:text-cyan-400 hover:underline"
                >
                  {t.cookieLegalNotice}
                </Link>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowCookieSettings(true)}
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                >
                  <Settings className="w-4 h-4" />
                  {t.cookieSettings}
                </button>
                <button
                  onClick={handleRejectAll}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                >
                  {t.cookieRejectAll}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 dark:bg-cyan-500 dark:hover:bg-cyan-600 text-white rounded-lg transition-colors font-medium"
                >
                  {t.cookieAcceptAll}
                </button>
              </div>
            </div>
          ) : (
            // Cookie Settings Modal
            <div className="p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t.cookieEditTitle}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t.cookieEditDescription}
              </p>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-start gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {t.cookieNecessary}
                      </h3>
                      <div className="w-6 h-6 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                        <Check className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t.cookieNecessaryDesc}
                    </p>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="flex items-start gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {t.cookieFunctional}
                      </h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCookie('functional');
                        }}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          cookiePreferences.functional
                            ? 'bg-purple-600 dark:bg-purple-500 border-purple-600 dark:border-purple-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {cookiePreferences.functional && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t.cookieFunctionalDesc}
                    </p>
                  </div>
                </div>

                {/* Analytical Cookies */}
                <div className="flex items-start gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {t.cookieAnalytical}
                      </h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCookie('analytical');
                        }}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          cookiePreferences.analytical
                            ? 'bg-blue-600 dark:bg-blue-500 border-blue-600 dark:border-blue-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {cookiePreferences.analytical && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t.cookieAnalyticalDesc}
                    </p>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <div className="w-5 h-5 rounded bg-orange-500"></div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {t.cookieMarketing}
                      </h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCookie('marketing');
                        }}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          cookiePreferences.marketing
                            ? 'bg-orange-600 dark:bg-orange-500 border-orange-600 dark:border-orange-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {cookiePreferences.marketing && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t.cookieMarketingDesc}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCookieSettings(false);
                    setShowCookieModal(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                >
                  {t.cookieBack}
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 dark:bg-cyan-500 dark:hover:bg-cyan-600 text-white rounded-lg transition-colors font-medium"
                >
                  {t.cookieSave}
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
        )}
      </>
    );
  };

  // Testimonials Scroll Section Component
  const TestimonialsScrollSection = () => {
    const containerRef = useRef(null);
    const cardsRef = useRef(null);
    const isInitializedRef = useRef(false);

    const testimonials = [
      {
        text: t.testimonial1Text || 'FeedPanels made managing customer feedback incredibly easy. Customer satisfaction increased by 40%.',
        name: t.testimonial1Name || 'John D.',
        company: t.testimonial1Company || 'Tech Startup',
        bgColor: 'bg-purple-100 dark:bg-purple-900/20',
        textColor: 'text-gray-800 dark:text-gray-200'
      },
      {
        text: t.testimonial2Text || 'Widget integration is very simple, our entire team can see feedback quickly. Amazing tool!',
        name: t.testimonial2Name || 'Sarah M.',
        company: t.testimonial2Company || 'SaaS Company',
        bgColor: 'bg-green-100 dark:bg-green-900/20',
        textColor: 'text-gray-800 dark:text-gray-200'
      },
      {
        text: t.testimonial3Text || 'Thanks to feedback analytics, our product development process accelerated by 50%. Love it!',
        name: t.testimonial3Name || 'Mike T.',
        company: t.testimonial3Company || 'E-Commerce',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
        textColor: 'text-gray-800 dark:text-gray-200'
      },
      {
        text: t.testimonial4Text || 'Collecting and managing customer feedback is now so easy. Our team and customers are very satisfied!',
        name: t.testimonial4Name || 'Emma L.',
        company: t.testimonial4Company || 'Digital Agency',
        bgColor: 'bg-blue-100 dark:bg-blue-900/20',
        textColor: 'text-gray-800 dark:text-gray-200'
      }
    ];

    useEffect(() => {
      // Küçük ekranlarda (md altında) scroll animasyonunu devre dışı bırak
      const isMobile = window.innerWidth < 768; // md breakpoint
      if (isMobile || !containerRef.current || !cardsRef.current || isInitializedRef.current) return;

      const container = containerRef.current;
      const cards = cardsRef.current;
      let tl = null;

      // ScrollTrigger'ı sadece bir kez oluştur
      const timer = setTimeout(() => {
        // Kartların toplam genişliğini hesapla
        const cardWidth = cards.children[0]?.offsetWidth || 500;
        const gap = 24; // gap-6 = 24px
        const totalWidth = (cardWidth + gap) * testimonials.length - gap;
        
        // Kartların başlangıç pozisyonu (daha sağdan başlasın)
        const startOffset = window.innerWidth * 0.3; // Ekranın %30'u kadar sağdan başla
        // Kartların bitiş pozisyonu: İlk kart tamamen gözükmediğinde, 2. ve 3. kartlar tamamen gözüksün
        // Başlangıçta 2. kartın sol kenarı: startOffset + cardWidth + gap
        // Bitişte 2. kartın sol kenarı ekranın sol kenarında (0) olmalı
        // Başlangıçta x: startOffset, bitişte x: -endOffset
        // 2. kartın sol kenarı başlangıçta: startOffset + cardWidth + gap
        // 2. kartın sol kenarı bitişte: startOffset - endOffset + cardWidth + gap = 0
        // endOffset = startOffset + cardWidth + gap
        // 2. kartın tamamen gözükmesi için kayma miktarı
        const endOffset = startOffset + cardWidth + gap;

        // Horizontal scroll animasyonu - kartların yarısı görünür olduğunda başla
        const scrollDistance = window.innerHeight * 0.55; // Biraz daha fazla scroll mesafesi
        
        tl = gsap.timeline({
          scrollTrigger: {
            trigger: container,
            start: "top center", // Container'ın üstü ekranın ortasına geldiğinde başla (kartların yarısı görünür)
            end: `+=${scrollDistance}`, // Yarım scroll'dan biraz fazla scroll mesafesi
            scrub: true, // Scroll ile anında senkronize (gecikme yok)
          }
        });

        // Kartları sağdan sola kaydır - başlangıçta sağda, bitişte daha solda
        tl.fromTo(cards, 
          {
            x: startOffset // Başlangıç: sağdan başla
          },
          {
            x: -endOffset, // Bitiş: daha sola git
            ease: "none"
          }
        );

        isInitializedRef.current = true;
      }, 100);

      return () => {
        clearTimeout(timer);
        if (tl) {
          tl.kill();
        }
        ScrollTrigger.getAll().forEach(st => {
          if (st.vars && st.vars.trigger === container) {
            st.kill();
          }
        });
        isInitializedRef.current = false;
      };
    }, [testimonials.length]);

    if (testimonials.length === 0) {
      return null;
    }

    return (
      <section className="relative py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-white to-cyan-50 dark:from-gray-800 dark:to-gray-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-8 sm:mb-12 md:mb-16 lg:mb-20 text-center">
          <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 md:mb-4 px-2">
            {t.testimonialsTitle}
          </h3>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 dark:text-gray-300 px-2 max-w-2xl mx-auto">
            {t.testimonialsSubtitle}
          </p>
        </div>
        
        {/* Mobile/Tablet View - Alt alta kartlar (md altında) */}
        <div className="md:hidden max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`flex flex-col rounded-2xl p-6 sm:p-8 ${testimonial.bgColor} shadow-lg dark:shadow-gray-900/50`}
              >
                <p className={`flex-1 text-base sm:text-lg md:text-xl ${testimonial.textColor} mb-6 sm:mb-8 leading-relaxed font-medium`}>
                  "{testimonial.text || ''}"
                </p>
                <div className="flex items-start gap-3 sm:gap-4 mt-auto">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0">
                    {testimonial.name && testimonial.name.length > 0 ? testimonial.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold text-sm sm:text-base md:text-lg ${testimonial.textColor}`}>
                      {testimonial.name || 'User'}
                    </p>
                    <p className={`text-xs sm:text-sm md:text-base ${testimonial.textColor} opacity-70`}>
                      {testimonial.company || ''}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop View - Horizontal scroll animasyonu (md ve üzeri) */}
        <div 
          ref={containerRef}
          className="hidden md:block relative w-full py-20 sm:py-24 md:py-32"
        >
          <div className="flex items-center overflow-x-hidden w-full">
            <div 
              ref={cardsRef}
              className="flex gap-6 px-4 sm:px-6 md:px-8 lg:px-12"
              style={{ willChange: 'transform' }}
            >
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`flex flex-col flex-shrink-0 w-[60vw] lg:w-[500px] xl:w-[600px] rounded-2xl p-8 sm:p-10 md:p-12 ${testimonial.bgColor} shadow-lg dark:shadow-gray-900/50`}
                >
                  <p className={`flex-1 text-lg sm:text-xl md:text-2xl lg:text-3xl ${testimonial.textColor} mb-6 sm:mb-8 md:mb-10 leading-relaxed font-medium`}>
                    "{testimonial.text || ''}"
                  </p>
                  <div className="flex items-start gap-4 sm:gap-5 mt-auto">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center text-white font-bold text-base sm:text-lg md:text-xl flex-shrink-0">
                      {testimonial.name && testimonial.name.length > 0 ? testimonial.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold text-base sm:text-lg md:text-xl ${testimonial.textColor}`}>
                        {testimonial.name || 'User'}
                      </p>
                      <p className={`text-sm sm:text-base md:text-lg ${testimonial.textColor} opacity-70`}>
                        {testimonial.company || ''}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      {/* htmlAttributes keeps <html lang> honest: the shell ships lang="en" and the
          page used to render Turkish under it. Now the tag follows the render. */}
      <Helmet htmlAttributes={{ lang: language }}>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content="customer feedback, feedback management, product feedback, customer surveys, feedback tool" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={language} />
        <meta property="og:url" content={langUrl(language)} />
        <meta property="og:image" content="https://feedpanels.com/logo-512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content="https://feedpanels.com/logo-512.png" />
        {/* Self-referencing canonical per variant: the audit flagged that pointing
            every language at the English URL tells Google not to index the others. */}
        <link rel="canonical" href={langUrl(language)} />
        {Object.keys(SEO_META).map((l) => (
          <link key={l} rel="alternate" hrefLang={l} href={langUrl(l)} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={LANDING_URL} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Organization',
                '@id': 'https://feedpanels.com/#organization',
                name: 'FeedPanels',
                url: 'https://feedpanels.com',
                logo: 'https://feedpanels.com/logo-512.png',
                description: 'Customer Feedback Management Platform. Collect, analyze and act on customer feedback.',
                // Profiles are all linked from the page already — declaring them here lets
                // search engines verify the brand entity instead of guessing.
                sameAs: SAME_AS_PROFILES,
              },
              {
                '@type': 'WebSite',
                '@id': 'https://feedpanels.com/#website',
                name: 'FeedPanels',
                url: 'https://feedpanels.com',
                description: 'Collect, analyze and act on customer feedback. FeedPanels helps you understand what your customers want.',
                publisher: { '@id': 'https://feedpanels.com/#organization' },
              },
              {
                '@type': 'SoftwareApplication',
                '@id': 'https://feedpanels.com/#software',
                name: 'FeedPanels',
                url: 'https://feedpanels.com',
                applicationCategory: 'BusinessApplication',
                operatingSystem: 'Web',
                description: 'Collect, analyze and act on customer feedback. Feedback board, roadmap and changelog in one platform.',
                publisher: { '@id': 'https://feedpanels.com/#organization' },
                // `offers` intentionally omitted: there is no standalone /pricing page to
                // cite, and inventing price data would be worse than declaring none.
              },
              faqJsonLd,
            ],
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      {/* Header */}
      <header
        ref={navbarRef}
        className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur border-b dark:border-gray-700 transition-all duration-300"
        style={{ transform: 'translateY(0)', opacity: '1', pointerEvents: 'auto' }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-1 sm:space-x-3 flex-shrink-0">
            <img
              src="/logo-256.webp"
              alt="FeedPanels Logo"
              fetchPriority="high"
              width="128"
              height="126"
              className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 object-contain"
            />
            <div className="hidden sm:block">
              <p className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white" aria-label={t.brand}>{t.brand}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 hidden md:block">{t.tagline}</p>
            </div>
          </div>

          <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4">
            {/* Navigation Links - Desktop (md and above) */}
            <nav className="hidden md:flex items-center space-x-2 lg:space-x-4">
              <button
                onClick={() => {
                  scrollToSection('how-it-works');
                  setMobileMenuOpen(false);
                }}
                className="text-gray-600 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 font-medium transition-colors text-sm px-2 py-1"
              >
                {t.navHowItWorks}
              </button>
              <button
                onClick={() => {
                  scrollToSection('features');
                  setMobileMenuOpen(false);
                }}
                className="text-gray-600 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 font-medium transition-colors text-sm px-2 py-1"
              >
                {t.navFeatures}
              </button>
              <button
                onClick={() => {
                  scrollToSection('pricing');
                  setMobileMenuOpen(false);
                }}
                className="text-gray-600 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 font-medium transition-colors text-sm px-2 py-1"
              >
                {t.navPricing}
              </button>
              <button
                onClick={() => {
                  const blogElement = document.querySelector('[data-blog-section]');
                  if (blogElement) {
                    blogElement.scrollIntoView({ behavior: 'smooth' });
                  }
                  setMobileMenuOpen(false);
                }}
                className="text-gray-600 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 font-medium transition-colors text-sm px-2 py-1"
              >
                {t.navBlog}
              </button>
            </nav>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white flex-shrink-0"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="w-4 h-4 sm:w-4 sm:h-4" />
              ) : (
                <Moon className="w-4 h-4 sm:w-4 sm:h-4" />
              )}
            </button>

            {/* Language Selector - Mobile Dropdown */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white flex-shrink-0"
                    aria-label="Select language"
                  >
                    <Globe className="w-4 h-4 sm:w-4 sm:h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 dark:bg-gray-800 dark:border-gray-700">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`cursor-pointer flex items-center space-x-2 dark:hover:bg-gray-700 ${
                        language === lang.code ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400' : ''
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="flex-1">{lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Language Selector - Hidden on small screens */}
            <div className="hidden sm:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center space-x-1 px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-xs sm:text-sm">
                    <Globe className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden md:inline">{currentLanguage.flag}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 dark:bg-gray-800 dark:border-gray-700">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`cursor-pointer flex items-center space-x-2 dark:hover:bg-gray-700 ${
                        language === lang.code ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400' : ''
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="flex-1">{lang.name}</span>
                      {language === lang.code && (
                        <Check className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Login Button - Hidden on very small screens */}
            <button
              onClick={() => navigate('/login')}
              className="hidden sm:inline text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors text-xs sm:text-sm px-2 py-1"
            >
              {t.signIn}
            </button>

            {/* Register Button */}
            <button
              onClick={() => navigate('/register')}
              className="bg-cyan-600 hover:bg-cyan-700 dark:bg-cyan-500 dark:hover:bg-cyan-600 text-white px-3 sm:px-5 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-xs sm:text-sm flex-shrink-0"
            >
              <span className="hidden sm:inline">{t.getStarted}</span>
              <span className="sm:hidden text-xs">{t.getStartedShort}</span>
            </button>

            {/* Mobile Menu Trigger - Visible only on md and below */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all text-gray-700 dark:text-gray-300">
                  {mobileMenuOpen ? (
                    <X className="w-5 h-5" />
                  ) : (
                    <Menu className="w-5 h-5" />
                  )}
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="dark:bg-gray-900 dark:border-gray-700">
                <div className="flex flex-col space-y-4 mt-8">
                  <button
                    onClick={() => {
                      scrollToSection('how-it-works');
                      setMobileMenuOpen(false);
                    }}
                    className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-lg transition-colors"
                  >
                    {t.navHowItWorks}
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('features');
                      setMobileMenuOpen(false);
                    }}
                    className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-lg transition-colors"
                  >
                    {t.navFeatures}
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('pricing');
                      setMobileMenuOpen(false);
                    }}
                    className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-lg transition-colors"
                  >
                    {t.navPricing}
                  </button>
                  <button
                    onClick={() => {
                      const blogElement = document.querySelector('[data-blog-section]');
                      if (blogElement) {
                        blogElement.scrollIntoView({ behavior: 'smooth' });
                      }
                      setMobileMenuOpen(false);
                    }}
                    className="text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-lg transition-colors"
                  >
                    {t.navBlog}
                  </button>
                  <div className="border-t dark:border-gray-700 pt-4">
                    <button
                      onClick={() => {
                        navigate('/login');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-lg transition-colors"
                    >
                      {t.signIn}
                    </button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-cyan-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16 sm:py-24 md:py-32 lg:py-40 transition-colors duration-300">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient Orbs */}
          <div className="absolute top-20 left-10 w-48 h-48 sm:w-64 sm:h-64 md:w-72 md:h-72 bg-cyan-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-40 right-10 w-48 h-48 sm:w-64 sm:h-64 md:w-72 md:h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-1/2 w-48 h-48 sm:w-64 sm:h-64 md:w-72 md:h-72 bg-teal-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-cyan-200 dark:border-cyan-800 rounded-full text-xs sm:text-sm font-medium text-cyan-700 dark:text-cyan-400 mb-6 sm:mb-8 shadow-sm hover:shadow-md transition-shadow">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-cyan-600 dark:text-cyan-400 animate-pulse" />
              <span className="hidden sm:inline">{t.heroBadge}</span>
              <span className="sm:hidden">{t.heroBadgeShort}</span>
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-cyan-600 dark:text-cyan-400" />
            </div>

            {/* Main Heading */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl xl:text-8xl font-extrabold mb-6 sm:mb-8 leading-tight px-2">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 via-blue-600 to-teal-600 dark:from-cyan-400 dark:via-blue-400 dark:to-teal-400 animate-gradient">
                {t.heroTitle}
              </span>
            </h1>

            {/* Description */}
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 dark:text-gray-300 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-2">
              {t.heroDescription}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-12 sm:mb-16 px-2">
              <button
                onClick={() => navigate('/register')}
                className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white rounded-xl font-bold text-base sm:text-lg flex items-center justify-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 w-full sm:w-auto"
              >
                <span className="relative z-10">{t.getStarted}</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity"></div>
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-cyan-300 dark:hover:border-cyan-600 hover:text-cyan-600 dark:hover:text-cyan-400 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1 w-full sm:w-auto"
              >
                {t.signIn}
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 sm:gap-6 md:gap-8 text-xs sm:text-sm text-gray-500 dark:text-gray-400 px-2">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                <span>{t.heroTrust1}</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                <span>{t.heroTrust2}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                <span>{t.heroTrust3}</span>
              </div>
            </div>

            {/* Product Badges */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mt-8 sm:mt-12 px-2">
              <a 
                href="https://www.producthunt.com/products/feedpanels-com?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-feedpanels-com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="transform hover:scale-105 transition-transform duration-300"
              >
                <img
                  src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1067990&theme=light"
                  alt="FeedPanels - Centralize customer feedback and turn insights into action | Product Hunt"
                  loading="lazy"
                  style={{ height: '50px', width: 'auto' }}
                  className="dark:hidden"
                />
                <img
                  src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1067990&theme=dark"
                  alt="FeedPanels - Centralize customer feedback and turn insights into action | Product Hunt"
                  loading="lazy"
                  style={{ height: '50px', width: 'auto' }}
                  className="hidden dark:block"
                />
              </a>
              <a
                href="https://www.uneed.best/tool/feed-panels"
                target="_blank"
                rel="noopener noreferrer"
                className="transform hover:scale-105 transition-transform duration-300"
              >
                <img
                  src="https://www.uneed.best/EMBED3.png"
                  alt="Uneed Embed Badge"
                  loading="lazy"
                  style={{ height: '60px', width: 'auto' }}
                />
              </a>
              <Link 
                to="/legal/privacy"
                className="transform hover:scale-105 transition-transform duration-300"
              >
                <img
                  src="/GDPR.avif"
                  alt="GDPR Compliant"
                  loading="lazy"
                  className="h-[110px] w-auto"
                />
              </Link>
            </div>

            {/* Floating Icons */}
            <div className="absolute top-20 left-10 hidden lg:block animate-float">
              <div className="w-16 h-16 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg flex items-center justify-center">
                <MessageSquare className="w-8 h-8 text-cyan-600" />
              </div>
            </div>
            <div className="absolute top-40 right-20 hidden lg:block animate-float animation-delay-2000">
              <div className="w-16 h-16 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg flex items-center justify-center">
                <BarChart3 className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="absolute bottom-20 left-20 hidden lg:block animate-float animation-delay-4000">
              <div className="w-16 h-16 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg dark:shadow-gray-900/50 flex items-center justify-center">
                <Star className="w-8 h-8 text-teal-600 dark:text-teal-400" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section - Lazy loaded, DE vs EN, 16:9 preserved, sharp placeholder */}
      <section
        ref={videoSectionRef}
        id="demo-video"
        className="py-10 sm:py-12 md:py-14 lg:py-16 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 border-t border-gray-100 dark:border-gray-700 scroll-mt-20 transition-colors duration-300"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6 sm:mb-8 md:mb-10">
            <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 px-2">
              {t.videoSectionTitle}
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 px-2 max-w-2xl mx-auto">
              {t.videoSectionSubtitle}
            </p>
          </div>
          {/* 16:9 aspect ratio container - do not change */}
          <div
            className="relative w-full rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-2xl dark:shadow-gray-900/50 ring-2 ring-cyan-500/10 dark:ring-cyan-400/10 hover:ring-cyan-500/20 dark:hover:ring-cyan-400/20 transition-all duration-300"
            style={{ paddingBottom: '56.25%' }}
          >
            {videoInView ? (
              <iframe
                title={language === 'de' ? 'FeedPanels Demo (Deutsch)' : 'FeedPanels Demo (English)'}
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${language === 'de' ? '5Vi4eKA6xAo' : 'lDZaEgmHLJ0'}?rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            ) : (
              /* Placeholder: CSS + SVG only, no raster – sharp at any resolution */
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-50 via-cyan-50/40 to-teal-50/40 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800">
                <div className="relative z-10 flex flex-col items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-xl shadow-cyan-500/25 dark:shadow-cyan-400/20 ring-4 ring-white/60 dark:ring-gray-700/60">
                    <Play className="w-10 h-10 text-white ml-1 shrink-0" fill="currentColor" strokeWidth={0} />
                  </div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.videoSectionLoading}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-gradient-to-b from-white to-cyan-50 dark:from-gray-800 dark:to-gray-900 py-12 sm:py-16 md:py-20 lg:py-24 scroll-mt-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 px-2">
              {t.howItWorksTitle}
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 px-2">
              {t.howItWorksSubtitle}
            </p>
          </div>

          <div className="relative">
            {/* Elegant Arrow Connections - Desktop */}
            <div className="hidden lg:flex absolute top-24 left-0 right-0 items-center justify-between px-12" style={{ zIndex: 0, height: '48px' }}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex-1 flex items-center relative" style={{ maxWidth: 'calc(33.333% - 2rem)' }}>
                  {/* Curved line */}
                  <svg className="w-full h-8" viewBox="0 0 200 32" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id={`arrowGradient${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#14b8a6" stopOpacity="0.6" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M 0 16 Q 50 8, 100 16 Q 150 24, 200 16"
                      stroke={`url(#arrowGradient${i})`}
                      strokeWidth="2.5"
                      fill="none"
                      strokeLinecap="round"
                    />
                    {/* Arrow head */}
                    <path
                      d="M 180 16 L 200 10 L 200 22 Z"
                      fill="#14b8a6"
                      opacity="0.7"
                    />
                    {/* Animated dot */}
                    <circle cx="100" cy="16" r="3" fill="#06b6d4" opacity="0.8">
                      <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="r" values="2;4;2" dur="2s" repeatCount="indefinite" />
                    </circle>
                  </svg>
                </div>
              ))}
            </div>

            {/* Elegant Arrow Connections - Tablet */}
            <div className="hidden md:flex lg:hidden absolute top-24 left-0 right-0 items-center justify-center px-8" style={{ zIndex: 0, height: '48px' }}>
              <svg className="w-full h-8" viewBox="0 0 600 32" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="arrowGradientMd" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.4" />
                    <stop offset="50%" stopColor="#14b8a6" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity="0.4" />
                  </linearGradient>
                </defs>
                <path
                  d="M 0 16 Q 150 8, 300 16 Q 450 24, 600 16"
                  stroke="url(#arrowGradientMd)"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                />
                <path d="M 580 16 L 600 10 L 600 22 Z" fill="#10b981" opacity="0.7" />
                <circle cx="300" cy="16" r="3" fill="#14b8a6" opacity="0.8">
                  <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite" />
                </circle>
              </svg>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 relative">
              {[
                { icon: UserPlus, title: t.step1Title, desc: t.step1Desc, color: 'from-blue-500 to-cyan-500' },
                { icon: Code, title: t.step2Title, desc: t.step2Desc, color: 'from-cyan-500 to-teal-500' },
                { icon: Plug, title: t.step3Title, desc: t.step3Desc, color: 'from-teal-500 to-green-500' },
                { icon: MessageCircle, title: t.step4Title, desc: t.step4Desc, color: 'from-green-500 to-emerald-500' },
              ].map((step, index) => (
                <StepCard key={index} step={step} index={index} />
              ))}
            </div>
          </div>

          {/* Feature Images Section */}
          <div className="mt-24 sm:mt-28 md:mt-32 lg:mt-40 space-y-32 sm:space-y-36 md:space-y-40 lg:space-y-48">
            {/* Feature 1: Feedback - Image Left, Text Right */}
            {(() => {
              const FeatureCard1 = () => {
                const featureRef = useRef(null);
                const [isVisible, setIsVisible] = useState(false);

                useEffect(() => {
                  const observer = new IntersectionObserver(
                    ([entry]) => {
                      if (entry.isIntersecting) {
                        setTimeout(() => {
                          setIsVisible(true);
                        }, 100);
                      }
                    },
                    { threshold: 0.1 }
                  );

                  if (featureRef.current) {
                    observer.observe(featureRef.current);
                  }

                  return () => {
                    if (featureRef.current) {
                      observer.unobserve(featureRef.current);
                    }
                  };
                }, []);

                return (
                  <div 
                    ref={featureRef}
                    className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12"
                  >
                    <div className={`flex-1 w-full lg:w-auto transition-all duration-700 ${
                      isVisible
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 -translate-x-10'
                    }`}>
                      <img 
                        src={isDark ? "/landingPageFeatureDarkMode/darkmodeFeedback.webp" : "/landigPagefeature/feedback.webp"}
                        alt={t.featureFeedbackTitle}
                        loading="lazy"
                        width={isDark ? 1651 : 1694}
                        height={isDark ? 1163 : 1190}
                        className="w-full rounded-xl shadow-2xl dark:shadow-gray-900/50 transform hover:scale-[1.02] transition-transform duration-500"
                      />
                    </div>
                    <div className={`flex-1 text-center lg:text-left transition-all duration-700 delay-200 ${
                      isVisible
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 translate-x-10'
                    }`}>
                      <h4 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                        {t.featureFeedbackTitle}
                      </h4>
                      <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                        {t.featureFeedbackDesc}
                      </p>
                    </div>
                  </div>
                );
              };
              return <FeatureCard1 />;
            })()}

            {/* Feature 2: Roadmap - Image Right, Text Left */}
            {(() => {
              const FeatureCard2 = () => {
                const featureRef = useRef(null);
                const [isVisible, setIsVisible] = useState(false);

                useEffect(() => {
                  const observer = new IntersectionObserver(
                    ([entry]) => {
                      if (entry.isIntersecting) {
                        setTimeout(() => {
                          setIsVisible(true);
                        }, 100);
                      }
                    },
                    { threshold: 0.1 }
                  );

                  if (featureRef.current) {
                    observer.observe(featureRef.current);
                  }

                  return () => {
                    if (featureRef.current) {
                      observer.unobserve(featureRef.current);
                    }
                  };
                }, []);

                return (
                  <div 
                    ref={featureRef}
                    className="flex flex-col lg:flex-row-reverse items-center gap-8 lg:gap-12"
                  >
                    <div className={`flex-1 w-full lg:w-auto transition-all duration-700 ${
                      isVisible
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 translate-x-10'
                    }`}>
                      <img 
                        src={isDark ? "/landingPageFeatureDarkMode/darkmoderoadmap.webp" : "/landigPagefeature/roadmap.webp"}
                        alt={t.featureRoadmapTitle}
                        loading="lazy"
                        width={isDark ? 1678 : 1699}
                        height={isDark ? 883 : 879}
                        className="w-full rounded-xl shadow-2xl dark:shadow-gray-900/50 transform hover:scale-[1.02] transition-transform duration-500"
                      />
                    </div>
                    <div className={`flex-1 text-center lg:text-left transition-all duration-700 delay-200 ${
                      isVisible
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 -translate-x-10'
                    }`}>
                      <h4 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                        {t.featureRoadmapTitle}
                      </h4>
                      <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                        {t.featureRoadmapDesc}
                      </p>
                    </div>
                  </div>
                );
              };
              return <FeatureCard2 />;
            })()}

            {/* Feature 3: Changelog - Image Left, Text Right */}
            {(() => {
              const FeatureCard3 = () => {
                const featureRef = useRef(null);
                const [isVisible, setIsVisible] = useState(false);

                useEffect(() => {
                  const observer = new IntersectionObserver(
                    ([entry]) => {
                      if (entry.isIntersecting) {
                        setTimeout(() => {
                          setIsVisible(true);
                        }, 100);
                      }
                    },
                    { threshold: 0.1 }
                  );

                  if (featureRef.current) {
                    observer.observe(featureRef.current);
                  }

                  return () => {
                    if (featureRef.current) {
                      observer.unobserve(featureRef.current);
                    }
                  };
                }, []);

                return (
                  <div 
                    ref={featureRef}
                    className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12"
                  >
                    <div className={`flex-1 w-full lg:w-auto transition-all duration-700 ${
                      isVisible
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 -translate-x-10'
                    }`}>
                      <img 
                        src={isDark ? "/landingPageFeatureDarkMode/darkmodechangelog.webp" : "/landigPagefeature/changelog.webp"}
                        alt={t.featureChangelogTitle}
                        loading="lazy"
                        width={isDark ? 1679 : 1695}
                        height={isDark ? 1013 : 1028}
                        className="w-full rounded-xl shadow-2xl dark:shadow-gray-900/50 transform hover:scale-[1.02] transition-transform duration-500"
                      />
                    </div>
                    <div className={`flex-1 text-center lg:text-left transition-all duration-700 delay-200 ${
                      isVisible
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 translate-x-10'
                    }`}>
                      <h4 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                        {t.featureChangelogTitle}
                      </h4>
                      <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                        {t.featureChangelogDesc}
                      </p>
                    </div>
                  </div>
                );
              };
              return <FeatureCard3 />;
            })()}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-white dark:bg-gray-800 py-12 sm:py-16 md:py-20 lg:py-24 border-t dark:border-gray-700 scroll-mt-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-8 sm:mb-12 md:mb-16 px-2">
            {t.featuresTitle}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const FeatureCard = () => {
                const cardRef = useRef(null);
                const [isVisible, setIsVisible] = useState(false);

                useEffect(() => {
                  const observer = new IntersectionObserver(
                    ([entry]) => {
                      if (entry.isIntersecting) {
                        setTimeout(() => {
                          setIsVisible(true);
                        }, index * 100);
                      }
                    },
                    { threshold: 0.1 }
                  );

                  if (cardRef.current) {
                    observer.observe(cardRef.current);
                  }

                  return () => {
                    if (cardRef.current) {
                      observer.unobserve(cardRef.current);
                    }
                  };
                }, [index]);

                return (
                  <div
                    ref={cardRef}
                    className={`group relative p-8 border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-800 hover:shadow-2xl dark:hover:shadow-gray-900/50 transition-all duration-500 overflow-hidden ${
                      isVisible
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-8'
                    }`}
                  >
                    {/* Background gradient on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${feature.bgGradient} dark:opacity-0 opacity-0 group-hover:opacity-100 dark:group-hover:opacity-20 transition-opacity duration-500`}></div>
                    
                    {/* Icon container with gradient */}
                    <div className="relative mb-6">
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                        <Icon className="w-8 h-8 text-white relative z-10 group-hover:scale-110 transition-transform duration-500" />
                      </div>
                      {/* Animated glow effect */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} rounded-2xl opacity-0 group-hover:opacity-30 dark:group-hover:opacity-20 blur-xl transition-opacity duration-500`}></div>
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors">
                        {feature.title}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{feature.desc}</p>
                    </div>

                    {/* Decorative corner accent */}
                    <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 dark:group-hover:opacity-5 rounded-bl-full transition-opacity duration-500`}></div>
                  </div>
                );
              };

              return <FeatureCard key={index} />;
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 scroll-mt-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-8 sm:mb-12 md:mb-16 px-2">
            {t.pricingTitle}
          </h3>
          <PricingPlans
            onPlanSelect={(plan) => navigate('/register')}
            showBillingToggle={true}
          />
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white dark:bg-gray-800 border-t dark:border-gray-700 scroll-mt-20 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 px-2">
              {t.faqTitle}
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 px-2">
              {t.faqSubtitle}
            </p>
          </div>

          <Tabs defaultValue="project" className="w-full">
            <TabsList className="inline-flex h-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 p-1 text-gray-500 dark:text-gray-400 mb-6 sm:mb-8 w-full">
              <TabsTrigger 
                value="project" 
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm dark:ring-offset-gray-950 dark:focus-visible:ring-gray-300 dark:data-[state=active]:bg-gradient-to-r dark:data-[state=active]:from-cyan-600 dark:data-[state=active]:to-blue-600 dark:data-[state=active]:text-white dark:data-[state=active]:shadow-lg dark:data-[state=active]:shadow-cyan-500/20 flex-1"
              >
                {t.faqTabProject}
              </TabsTrigger>
              <TabsTrigger 
                value="payment" 
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm dark:ring-offset-gray-950 dark:focus-visible:ring-gray-300 dark:data-[state=active]:bg-gradient-to-r dark:data-[state=active]:from-cyan-600 dark:data-[state=active]:to-blue-600 dark:data-[state=active]:text-white dark:data-[state=active]:shadow-lg dark:data-[state=active]:shadow-cyan-500/20 flex-1"
              >
                {t.faqTabPayment}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="project" className="mt-0">
              <Accordion type="single" collapsible className="w-full space-y-4">
                {projectFaqs.map((faq, index) => {
                  const FaqItemWrapper = () => {
                    const wrapperRef = useRef(null);
                    const [isVisible, setIsVisible] = useState(false);

                    useEffect(() => {
                      const observer = new IntersectionObserver(
                        ([entry]) => {
                          if (entry.isIntersecting) {
                            setTimeout(() => {
                              setIsVisible(true);
                            }, index * 100);
                          }
                        },
                        { threshold: 0.1 }
                      );

                      if (wrapperRef.current) {
                        observer.observe(wrapperRef.current);
                      }

                      return () => {
                        if (wrapperRef.current) {
                          observer.unobserve(wrapperRef.current);
                        }
                      };
                    }, [index]);

                    return (
                      <div
                        ref={wrapperRef}
                        className={`transition-all duration-500 ${
                          isVisible
                            ? 'opacity-100 translate-x-0'
                            : 'opacity-0 -translate-x-4'
                        }`}
                      >
                        <AccordionItem
                          value={`project-item-${index}`}
                          className="border border-gray-200 dark:border-gray-700 rounded-xl px-4 sm:px-6 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md dark:hover:shadow-gray-900/50 transition-all duration-300 relative overflow-hidden group"
                        >
                          {/* Decorative gradient on hover */}
                          <div className="absolute inset-0 bg-gradient-to-r from-cyan-50/50 to-blue-50/50 dark:from-cyan-900/20 dark:to-blue-900/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                          
                          <AccordionTrigger className="text-left font-semibold text-sm sm:text-base text-gray-900 dark:text-white hover:no-underline py-4 sm:py-6 relative z-10 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors duration-300">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-600 dark:text-gray-300 pb-4 sm:pb-6 pt-0 relative z-10 text-sm sm:text-base">
                            <div className="leading-relaxed">{faq.answer}</div>
                          </AccordionContent>
                        </AccordionItem>
                      </div>
                    );
                  };

                  return <FaqItemWrapper key={index} />;
                })}
              </Accordion>
            </TabsContent>

            <TabsContent value="payment" className="mt-0">
              <Accordion type="single" collapsible className="w-full space-y-4">
                {paymentFaqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`payment-item-${index}`}
                    className="border border-gray-200 dark:border-gray-700 rounded-xl px-4 sm:px-6 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <AccordionTrigger className="text-left font-semibold text-sm sm:text-base text-gray-900 dark:text-white hover:no-underline py-4 sm:py-6">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 dark:text-gray-300 pb-4 sm:pb-6 pt-0 text-sm sm:text-base">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Blog Section */}
      <BlogSection />

      {/* Testimonials Section with Horizontal Scroll */}
      <TestimonialsScrollSection />

      {/* Contact Section */}
      <section ref={contactSectionRef} id="contact" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white dark:bg-gray-800 border-t dark:border-gray-700 transition-colors duration-300 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-14 md:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-5 px-2">
              {t.contactSection}
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 px-2 max-w-2xl mx-auto">
              {t.contactSectionDesc}
            </p>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-2xl p-6 sm:p-8 md:p-10 lg:p-12 bg-white dark:bg-gray-800/50 backdrop-blur-sm shadow-lg dark:shadow-gray-900/50">
            <form onSubmit={handleSubmit} onInvalid={handleInvalid} className="space-y-5 sm:space-y-6 md:space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 md:gap-8">
                <div className="space-y-2.5">
                  <Label htmlFor="name" className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                    {t.yourName} *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t.yourNamePlaceholder}
                    required
                    title={t.fieldRequiredName}
                    className="text-sm h-10 sm:h-11 bg-gray-50 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-xs dark:placeholder:text-xs placeholder:text-gray-400 dark:placeholder:text-gray-500 focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 transition-all"
                  />
                </div>
                <div className="space-y-2.5">
                  <Label htmlFor="email" className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                    {t.email} *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.emailPlaceholder}
                    required
                    title={t.fieldRequiredEmail}
                    className="text-sm h-10 sm:h-11 bg-gray-50 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-xs dark:placeholder:text-xs placeholder:text-gray-400 dark:placeholder:text-gray-500 focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2.5">
                <Label htmlFor="subject" className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                  {t.subject} *
                </Label>
                <Input
                  id="subject"
                  type="text"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t.subjectPlaceholder}
                  required
                  title={t.fieldRequiredSubject}
                  className="text-sm h-10 sm:h-11 bg-gray-50 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-xs dark:placeholder:text-xs placeholder:text-gray-400 dark:placeholder:text-gray-500 focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 transition-all"
                />
              </div>

              <div className="space-y-2.5">
                <Label htmlFor="message" className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                  {t.message} *
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t.messagePlaceholder}
                  rows={6}
                  required
                  title={t.fieldRequiredMessage}
                  className="text-sm min-h-[120px] sm:min-h-[140px] bg-gray-50 dark:bg-gray-900/50 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-xs dark:placeholder:text-xs placeholder:text-gray-400 dark:placeholder:text-gray-500 focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 transition-all resize-none"
                />
              </div>

              {/* Privacy Policy Checkbox */}
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="acceptPrivacy"
                    checked={acceptPrivacy}
                    onChange={(e) => setAcceptPrivacy(e.target.checked)}
                    className="mt-1 w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500 dark:focus:ring-cyan-400 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 flex-shrink-0"
                    required
                  />
                  <label
                    htmlFor="acceptPrivacy"
                    className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 cursor-pointer leading-relaxed"
                  >
                    {language === 'tr' && (
                      <>
                        <Link
                          to={getLegalDocumentUrl('privacy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Gizlilik Politikası
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400">'nı okudum ve verilerimin talebimin işlenmesi için kullanılmasını kabul ediyorum. *</span>
                      </>
                    )}
                    {language === 'en' && (
                      <>
                        <span className="text-gray-600 dark:text-gray-400">I have read the </span>
                        <Link
                          to={getLegalDocumentUrl('privacy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Privacy Policy
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400"> and accept that my data will be used to process my request. *</span>
                      </>
                    )}
                    {language === 'de' && (
                      <>
                        <span className="text-gray-600 dark:text-gray-400">Ich habe die </span>
                        <Link
                          to={getLegalDocumentUrl('privacy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Datenschutzerklärung
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400"> gelesen und akzeptiere, dass meine Daten zur Bearbeitung meiner Anfrage verwendet werden. *</span>
                      </>
                    )}
                    {language === 'es' && (
                      <>
                        <span className="text-gray-600 dark:text-gray-400">He leído la </span>
                        <Link
                          to={getLegalDocumentUrl('privacy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Política de Privacidad
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400"> y acepto que mis datos se utilicen para procesar mi solicitud. *</span>
                      </>
                    )}
                    {language === 'fr' && (
                      <>
                        <span className="text-gray-600 dark:text-gray-400">J'ai lu la </span>
                        <Link
                          to={getLegalDocumentUrl('privacy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Politique de Confidentialité
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400"> et j'accepte que mes données soient utilisées pour traiter ma demande. *</span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* reCAPTCHA Widget */}
              <div className="space-y-2">
                <div
                  key={language}
                  id="recaptcha-container"
                  ref={recaptchaRef}
                  className="min-h-[78px] flex items-center justify-start"
                ></div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-500 dark:to-blue-500 hover:from-cyan-700 hover:to-blue-700 dark:hover:from-cyan-600 dark:hover:to-blue-600 disabled:from-gray-400 disabled:to-gray-400 dark:disabled:from-gray-600 dark:disabled:to-gray-600 text-white px-5 py-2.5 rounded-lg font-medium text-xs sm:text-sm flex items-center justify-center gap-1.5 transition-all duration-300 shadow-md hover:shadow-lg dark:shadow-cyan-500/20 hover:scale-[1.01] active:scale-[0.99]"
              >
                {loading ? (
                  <span className="flex items-center gap-1.5">
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs sm:text-sm">{t.sending}</span>
                  </span>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>{t.sendMessage}</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-cyan-700 py-12 sm:py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 px-2">
            {t.ctaTitle}
          </h3>
          <p className="text-base sm:text-lg text-cyan-100 mb-6 sm:mb-8 px-2">
            {t.ctaDescription}
          </p>
          <button
            onClick={() => navigate('/register')}
            className="bg-white text-cyan-600 hover:bg-gray-100 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg transition-colors w-full sm:w-auto"
          >
            {t.getStarted}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-black text-gray-50 dark:text-gray-300 border-t border-gray-800 dark:border-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {/* Brand Section */}
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                <img
                  src="/logo-256.webp"
                  alt="FeedPanels Logo"
                  loading="lazy"
                  width="128"
                  height="126"
                  className="w-28 h-28 sm:w-32 sm:h-32 object-contain"
                />
                <div>
                  <h4 className="text-lg sm:text-xl font-bold text-white">{t.brand}</h4>
                  <p className="text-xs text-gray-400">{t.tagline}</p>
                </div>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6 max-w-md">
                {t.footerDescription}
              </p>
              {/* Social Links */}
              <div className="flex items-center space-x-3 sm:space-x-4">
                <a
                  href="https://www.youtube.com/@feedpanels"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-gray-800 dark:bg-gray-900 hover:bg-cyan-600 dark:hover:bg-cyan-700 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-white transition-all duration-300 hover:scale-110"
                  aria-label="YouTube"
                >
                  <Youtube className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/feedpanels/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-gray-800 dark:bg-gray-900 hover:bg-cyan-600 dark:hover:bg-cyan-700 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-white transition-all duration-300 hover:scale-110"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61587291293117"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-gray-800 dark:bg-gray-900 hover:bg-cyan-600 dark:hover:bg-cyan-700 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-white transition-all duration-300 hover:scale-110"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h5 className="text-white dark:text-gray-100 font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{t.footerProduct}</h5>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a href="#features" className="text-gray-400 dark:text-gray-500 hover:text-cyan-400 dark:hover:text-cyan-300 transition-colors text-xs sm:text-sm">
                    {t.footerLinks.features}
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="text-gray-400 dark:text-gray-500 hover:text-cyan-400 dark:hover:text-cyan-300 transition-colors text-xs sm:text-sm">
                    {t.footerLinks.pricing}
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h5 className="text-white dark:text-gray-100 font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{t.footerCompany}</h5>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a
                    href="#blog"
                    onClick={(e) => {
                      e.preventDefault();
                      const blogElement = document.querySelector('[data-blog-section]');
                      if (blogElement) {
                        blogElement.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="text-gray-400 dark:text-gray-500 hover:text-cyan-400 dark:hover:text-cyan-300 transition-colors text-xs sm:text-sm cursor-pointer"
                  >
                    {t.footerLinks.blog}
                  </a>
                </li>
                <li>
                  <a
                    href="#contact"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('contact');
                    }}
                    className="text-gray-400 dark:text-gray-500 hover:text-cyan-400 dark:hover:text-cyan-300 transition-colors text-xs sm:text-sm cursor-pointer"
                  >
                    {t.footerLinks.contact}
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h5 className="text-white dark:text-gray-100 font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{t.footerLegal}</h5>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a 
                    href={getLegalDocumentUrl('privacy')} 
                    className="text-gray-400 dark:text-gray-500 hover:text-cyan-400 dark:hover:text-cyan-300 transition-colors text-xs sm:text-sm"
                  >
                    {t.footerLinks.privacy}
                  </a>
                </li>
                <li>
                  <a 
                    href={getLegalDocumentUrl('terms')} 
                    className="text-gray-400 dark:text-gray-500 hover:text-cyan-400 dark:hover:text-cyan-300 transition-colors text-xs sm:text-sm"
                  >
                    {t.footerLinks.terms}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-6 sm:pt-8 mt-6 sm:mt-8">
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <p className="text-gray-400 text-xs sm:text-sm text-center">
                {language === 'tr' && (
                  <>
                    © {new Date().getFullYear()}{' '}
                    <a
                      href="https://zeuz-it.de/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-400 transition-colors font-medium"
                    >
                      ZeuZ IT
                    </a>
                    {' tarafından oluşturuldu'}
                  </>
                )}
                {language === 'en' && (
                  <>
                    © {new Date().getFullYear()} Created by{' '}
                    <a
                      href="https://zeuz-it.de/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-400 transition-colors font-medium"
                    >
                      ZeuZ IT
                    </a>
                  </>
                )}
                {language === 'de' && (
                  <>
                    © {new Date().getFullYear()} Erstellt von{' '}
                    <a
                      href="https://zeuz-it.de/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-400 transition-colors font-medium"
                    >
                      ZeuZ IT
                    </a>
                  </>
                )}
                {language === 'es' && (
                  <>
                    © {new Date().getFullYear()} Creado por{' '}
                    <a
                      href="https://zeuz-it.de/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-400 transition-colors font-medium"
                    >
                      ZeuZ IT
                    </a>
                  </>
                )}
                {language === 'fr' && (
                  <>
                    © {new Date().getFullYear()} Créé par{' '}
                    <a
                      href="https://zeuz-it.de/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-400 transition-colors font-medium"
                    >
                      ZeuZ IT
                    </a>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Modal */}
      <CookieConsent />
    </div>
    </>
  );
};
